/// <reference types="jquery" />
/// <reference types="kendo-ui" />

import type { ModelynEditor } from "../../scripts/modelyn-editor/min/modelyn-editor.js";

// Lit - setup globals first (must be before any component that reads window.LitElement via lit-global.ts)
import './lit-setup';
//import { LitElement, css, html } from "lit";
import { html, css, render, LitElement } from "lit-element";
import { unsafeHTML } from "lit-html/directives/unsafe-html.js";
import { ref as litRef } from 'lit/directives/ref.js';
import { styleMap } from "lit-html/directives/style-map.js";

var global = window as any; /// this = window

// Import m-ico dist (reads window.LitElement at load - set by lit-setup above)
import { Mico } from '../modelyn-modules/dist/m-ico.js';
export { Mico } from '../modelyn-modules/dist/m-ico.js';

class LoaderInfo {
	sync?: boolean = false;
	prio?: number = 30;
	name: string;
	loader: () => Promise<void>;
}

declare global {
	//var unsafeHTML: typeof unsafeHTML;

	export interface IMdlnError {
		msg: string;
		url: string;
		line: number;
		col: number;
		errorObj: Error;
	}

	export interface TagsOptions {
		id: string;
		maxShown: number;
		maxSelectedItems: number;
		postbackOnClose: boolean;
		placeholder: string;
		keepOpen: boolean;
		tagTemplateSingle: string;
		tagTemplate: string;
		itemTemplate: string;
		footerTemplate: string;
		onClick: () => void;
		onInit: () => void;
	}

	export interface SpinnerOptions {
		/** The element to cover and add to. Null is screen */
		container?: JQuery<HTMLElement> | string | HTMLElement;
		/** The default timeout in ms before removing it. Default is 0 (never remove).*/
		timeout?: number;
		/** If set this HTML will be used for the modal */
		template?: string;
	}

	export interface PostProtectorOptions {
		/** The default timeout in ms before allowing another submit. The default is 600 ms */
		timeout?: number;
		/** The ms to wait before showing the loader. The default is no wait */
		wait?: number;
		/** On AJAX calls settings this to true will revert to default then done. 0 is endless */
		once?: boolean;
		/** If true a spinner will be added to the calling button */
		button?: boolean;
		/** If set this HTML will be used for the button spinner */
		buttonTemplate?: string;
		/** If true a modal spinner is dispayed on the entire screen */
		modal?: boolean;
		/** An optional function that if returns false wont show modal */
		modalIf?: Function;
		/** If set this HTML will be used for the modal */
		modalTemplate?: string;
	}

	export interface ParallaxOptions {
		media: string | HTMLImageElement | HTMLVideoElement;
		type: string;
		speed?: number;
		focal?: string;
		src?: string;
	}

	export interface LoadMediaOptions {
		dev?: boolean;
		skeleton?: boolean;
	}

	export interface LoadMediaOptionsSingle extends LoadMediaOptions {
		src?: string;
	}

	export interface BlankImageOptions {
		width?: number;
		height?: number;
		color?: string;
		opacity?: number;
	}

	export interface ResImgInfo {
		w: number, h: number;
		src: string;
	}

	export interface NotifyOptions {
		header?: string;
		theme?: "gray" | "primary" | "info" | "success" | "warning" | "error" | (string & {});
		position?: "top-right" | "top-left" | "bottom-right" | "bottom-left" | "center";
		thumb?: ":info" | ":success" | ":warning" | ":danger" | ":error" | ":notify" | ":email" | ":process" | ":schedule" | ":delete" | ":dispose" | ":debug" | ":connection" | ":chat" | (string & {});
		sticky?: boolean;
		life?: number;
	}

}


const N = null; // short for null
const BS = "esm!/scripts/bootstrap/dist/bootstrap.js";

// NOTE: Consider making Modelyn a "namespace" instead of a "class"
class Modelyn {
	static isAdminPage: boolean;

	public static readonly messagingTokenKey = "mdln.messaging.token";
	public static readonly deviceIdKey = "mdln.deviceId";
	public static MessagingClass: any;

	// -------------
	/// Messaging
	// -------------
	public static messaging: IModelynMessaging;
	//static Messaging: IModelynMessagingStatic;
	static async startMessaging(options: IMessagingOptions): Promise<IModelynMessaging> {
		options = options || {};
		if (Modelyn.messaging) {
			return Modelyn.messaging;
		}
		if ((options.noRequest && (Modelyn.messaging || window.localStorage.getItem(this.messagingTokenKey) || this.messagingPermission !== Modelyn.Messaging.Permissions.granted))) {
			return Modelyn.messaging;
		}
		await import(this.addEtag('../../scripts/modelyn-messaging/modelyn-messaging.js'));
		Modelyn.messaging = new global.Modelyn.MessagingClass(options) as IModelynMessaging;
		await Modelyn.messaging.run();
		return Modelyn.messaging;
	}

	public static get messagingPermission(): Modelyn.Messaging.Permissions {
		if (!Reflect.has(window, "Notification")) {
			//console.debug("This browser does not support notifications");
			return Modelyn.Messaging.Permissions.notSupported;
		}
		//console.debug("This browser supports notifications!");
		switch (Notification.permission) {
			case "granted": return Modelyn.Messaging.Permissions.granted;
			case "denied": return Modelyn.Messaging.Permissions.denied;
			case "default": return Modelyn.Messaging.Permissions.ask;
			default: return Modelyn.Messaging.Permissions.notSupported;
		}
	}

	private static codeHighlighter: ICodeHighlighter;
	private static codeHighlighterConfig: HighlighterConfig;
	private static _styleSheetCache = new Map<string, Promise<CSSStyleSheet>>();

	/**
	 * Gets or creates a CSSStyleSheet for adoptedStyleSheets usage (e.g. Shadow DOM).
	 * Caches per URL to prevent duplicate fetches on concurrent calls.
	 */
	public static getStyleSheet(url: string): Promise<CSSStyleSheet> {
		if (!this._styleSheetCache.has(url)) {
			this._styleSheetCache.set(url, (async () => {
				const fullUrl = url.includes('?') ? url : `${url}?etag=${this.etag}`;
				const response = await fetch(fullUrl);
				const cssText = await response.text();
				const sheet = new CSSStyleSheet();
				sheet.replaceSync(cssText);
				return sheet;
			})());
		}
		return this._styleSheetCache.get(url)!;
	}

	public static async addShadowDomSheet(rootNode, url: string): Promise<void> {
		// Shadow DOM: use adoptedStyleSheets since <link> tags in document.head won't penetrate
		const sheet = await this.getStyleSheet(url);
		if (!rootNode.adoptedStyleSheets.includes(sheet)) {
			rootNode.adoptedStyleSheets = [...rootNode.adoptedStyleSheets, sheet];
		}
	}

	public static async codeHighlight(options: HighlightExecuteOption, config?: HighlighterConfig): Promise<void> {
		const run = async () => {
			if (options?.element) {
				await this.codeHighlighter.highlight(options.element, options);
				return;
			}
			await this.codeHighlighter.highlightAll(options);
		};
		if (this.codeHighlighter) {
			await run();
			return;
		}

		// Load highlight styles
		const cssUrl = `/client/scss/parts/code/code.${this.getTheme()}.scss.css`;
		const rootNode = options?.host?.getRootNode?.();
		const isShadowDom = rootNode instanceof ShadowRoot;
		if (isShadowDom) {
			await this.addShadowDomSheet(rootNode, cssUrl);
		} else {
			// Regular DOM: just load the CSS normally via <link> tag
			await this.requireAsync(`css!${cssUrl}`);
		}

		const CodeHighlighter = (await import(this.addEtag('../../scripts/highlight/dist/highlight.js'))).default;
		this.codeHighlighter = new CodeHighlighter(config ?? this.codeHighlighterConfig);
		await run();
	}

	public static setCodeHighlightConfig(config: HighlighterConfig) {
		this.codeHighlighterConfig = config;
		this.codeHighlighter = null;
	}

	static theme: string;
	public static getTheme() {
		if (!this.theme) {
			this.theme = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
		}
		return this.theme;
	}

	// LEGACY
	/**
	 * DEPRECATED!
	 * @private
	 * @deprecated Deprecated
	 */
	static Alert(message: string) { console.error("Please use Modelyn.Dialog.Alert instead"); alert(message); }

	// LEGACY
	/**
	 * DEPRECATED!
	 * @private
	 * @deprecated Deprecated
	 */
	static Prompt(message, text) { console.error("Please use Modelyn.Dialog.Show instead"); return prompt(message, text); }

	// LEGACY
	/**
	 * DEPRECATED!
	 * @private
	 * @deprecated Deprecated
	 */
	static Confirm(message) { console.error("Please use Modelyn.Dialog.Show instead"); return confirm(message); }


	static modules = {
		bootstrap: {
			all: BS,
			modal: BS,
			carousel: BS,
			collapse: BS,
			dropdown: BS,
			offcanvas: BS,
			scrollspy: BS,
			popover: BS,
			dialog: "iife!/scripts/ui/bootbox.js",
		},
		kendo: {
			dom: N,
			template: "kendo.core",
			core: N,
			fx: N,
			userevents: N,
			icons: N,
			list: N,
			treeview: {
				toString: () => "treeview",
				draganddrop: N,
			},
			treelist: N,
			virtuallist: N,
			skeletoncontainer: N,
			popup: N,
			bottomnavigation: N,
			expansionpanel: N,
			floatingactionbutton: N,
			window: N,
			dialog: N,
			tooltip: N,
			draganddrop: N,
			splitter: N,
			drawer: N,
			resizable: N,
			combobox: N,
			multicolumncombobox: N,
			multiselect: N,
			dropdownlist: N,
			dropdowntree: N,
			slider: N,
			checkbox: N,
			dateinput: N,
			datepicker: N,
			timepicker: N,
			datetimepicker: N,
			daterangepicker: N,
			calendar: N,
			multiviewcalendar: N,
			editable: N,
			binder: N,
			badge: N,
			form: N,
			label: N,
			floatinglabel: N,
			textbox: N,
			toggleinputbase: N,
			togglebutton: N,
			button: {
				toString: () => "button",
				menu: N,
			},
			buttongroup: N,
			dropdownbutton: N,
			splitbutton: N,
			tilelayout: N,
			stepper: N,
			menu: N,
			listview: N,
			listbox: N,
			selectable: N,
			validator: N,
			numerictextbox: N,
			tabstrip: N,
			autocomplete: N,
			color: N,
			colorpicker: N,
			drawing: N,
			grid: N,
			pane: N,
			columnsorter: N,
			filtermenu: N,
			filtercell: N,
			columnmenu: N,
			groupable: N,
			pager: N,
			sortable: N,
			reorderable: N,
			view: N,
			ooxml: N,
			excel: N,
			progressbar: N,
			editor: N,
			breadcrumb: N,
			filebrowser: N,
			imagebrowser: N,
			upload: N,
			pdf: N,
			ripple: N,
			switch: N,
			scrollview: N,
			pdfviewer: N,
			timeline: N,
			popover: N,
			scheduler: {
				toString: () => "scheduler",
				recurrence: N,
				view: N,
				dayview: N,
				yearview: N,
				agendaview: N,
				monthview: N,
				timelineview: N,
			},
			rating: N,
			toolbar: N,
			appbar: N,
			imageeditor: N,
			loader: N,
			textarea: N,
			filemanager: N,
			taskboard: N,
			touch: N,
			gantt: {
				toString: () => "gantt",
				data: N,
				editors: N,
				list: N,
				timeline: N,
			},
			html: {
				toString: () => "html",
				base: N,
				button: N,
				input: N,
				chip: N,
				chiplist: N,
				icon: N,
			},
			data: {
				toString: () => "data",
				odata: N,
				xml: N,
				signalr: N,
			},
			mobile: {
				toString: () => "mobile",
				scroller: N,
				actionsheet: N,
				pane: N,
				popover: N,
				shim: N,
				loader: N,
				view: N,
			},
			dataviz: {
				toString: () => "dataviz",
				core: N,
				themes: N,
				gauge: N,
				chart: N,
				sparkline: N,
				stock: N,
				treemap: N,
				qrcode: N,
				barcode: N,
				diagram: N,
			},
		}
	};

	//static Dialog: Modelyn.Dialog;

	static fef: string;
	static isAjax: boolean;
	static isPostback: boolean;
	static isDebugMode: boolean;
	static culture: string;
	static language: string;
	static userId: string;
	static userKey: string;
	static decimalSeparator: string = ".";
	static thousandSeparator: string = ",";
	static domainPath: string = "/";
	//global: any = window;
	static kendoThemePath: string;
	static etag: string;
	static ignoreFocus: boolean;
	static inAdmin: boolean;

	/**
	* Test will do stuff
	 *
	 * Yes!
	* @constructor
	* @param {number} value - The value parameter!
	* @return {number} Returns stuff!
	*/
	static testEditorDocumentation(value) {
		return 0;
	}

	static errors: IMdlnError[];
	static errorListener: () => void;



	static onResize() {
		const vw = (global.isPle && global.Editor?.root ? global.Editor.root.hobj : document.documentElement).clientWidth;
		document.documentElement.style.setProperty("--vw1", `${vw / 100}px`);
		document.documentElement.style.setProperty("--vw100", `${vw}px`);

		// Container width
		const containers = document.querySelectorAll(".container");
		containers.forEach((container: HTMLElement) => {
			container.style.setProperty("--mdln-container-offset-left", container.offsetLeft.toString());
			container.style.setProperty("--mdln-container-width", container.offsetWidth.toString());
		});
	}

	//static async installWorker() {
	//	if ('serviceWorker' in navigator) {
	//		// Modern browsers: SW handles it globally
	//		const reg = await navigator.serviceWorker.register('/modelyn-sw.js');
	//		await navigator.serviceWorker.ready;
	//		navigator.serviceWorker.controller?.postMessage({
	//			type: 'SET_ETAG',
	//			etag: this.etag,
	//			isDebug: this.isDebugMode
	//		});
	//	}
	//}

	static init() {
		global.Modelyn = Modelyn;
		// LEGACY
		global.Cpo = Modelyn;

		//this.installWorker();

		// Make sure its there - if no form and no Page.MaintainScrollPositionOnPostBack = false etc.
		document.addEventListener("DOMContentLoaded", () => {
			global.theForm = global.theForm || document.forms[0];

			this.onResize();
			window.addEventListener("resize", this.onResize);

			document.documentElement.className = document.documentElement.className.replace(/(^|\s)no-js(\s|$)/, "$1$2") + " js ";
		});

		window.onerror = (msg: string, url: string, line: number, col: number, errorObj: Error) => {
			if (typeof msg === 'string' && msg.includes('ResizeObserver')) return true;
			if (this.errors == null)
				this.errors = [];
			this.errors.push({ msg: msg, url: url, line: line, col: col, errorObj: errorObj });
			if (this.errorListener) {
				this.errorListener();
			}
		};

		const resolveModules = (modules, prefix: string) => {
			for (let key in modules) {
				let mod = modules[key];
				if (typeof mod === "function" || typeof mod === "string")
					continue;
				let name = (prefix ? prefix + "." : "") + (mod ?? key);
				if (mod !== null && typeof mod === "object") {
					resolveModules(mod, (prefix ? prefix + "." : "") + key);
				}
				else {
					modules[key] = name;
				}
			}
		}
		//"bootstrap": "/node_modules/bootstrap/dist/js/bootstrap.bundle",

		//console.log(JSON.stringify(this.modules));
		resolveModules(this.modules, null);
		//console.log(JSON.stringify(this.modules));


	}

	// LEGACY
	/**
	 * DEPRECATED!
	 * @private
	 * @deprecated Deprecated
	 */
	static Require(modules: string[], ready: (...loadedModules: any[]) => void): void {
		console.error("Method `Require` has been deprecated!");
		this.require(modules, loadedModules => { if (ready) ready(...loadedModules); });
	}

	private static extendModules(modules: string[], names: string[], withModules: string[], at: number = 0): void {
		if (names) {
			let found = false;
			for (let i = 0; i < names.length; i++) {
				if (modules.indexOf(names[i]) > -1) {
					found = true;
					break;
				}
			}
			if (!found) {
				return;
			}
		}
		for (let i = 0; i < withModules.length; i++) {
			let name = withModules[i];
			//if (name === "kendo.data") { }
			if (modules.indexOf(name) < 0) {
				if (at < 0) {
					modules.push(name);
				}
				else {
					modules.splice(at, 0, name);
				}
			}
		}
	}

	// LEGACY
	/**
	 * DEPRECATED!
	 * @private
	 * @deprecated Deprecated
	 */
	static RequireKendo(modules: string[], ready: (...loadedModules: any[]) => void): void {
		console.error("Method `RequireKendo` has been deprecated!");
		this.require(modules, ready);
	}

	static require(modules: string[], ready: (...loadedModules: any[]) => void = null): void {
		this.requireAsync(modules).then(loadedModules => {
			if (ready) ready(...loadedModules);
		});
	}

	static async requireKendoAsync(modules: string[] | string | Function | any[]): Promise<void> {
		await this.requireAsync(modules);
	}

	static requireKendo(modules: string[] | string | Function, ready: (...loadedModules: any[]) => void): void {
		this.requireAsync(modules).then(loadedModules => {
			if (ready) ready(...loadedModules);
		});
	}

	public static moduleCache = new Map<string, Promise<any>>();
	private static didKendoGlobal: boolean;
	private static didKendoModules = false;

	// Async version
	static async requireAsync(loadModules: string[] | string | Function | any[]): Promise<any | any[]> {
		let mods: string[] = [];
		if (!loadModules) {
			throw new Error("Invalid modules: " + loadModules); return;
		}
		//if (!Array.isArray(modules) && typeof modules === "object")
		//	modules = "kendo." + modules.toString(); // from modules.kendo
		let moduleCount = -1;
		if (typeof loadModules === "function") {
			mods = loadModules();
			moduleCount = 1;
		}
		if (typeof loadModules === "string") {
			mods = [loadModules];
			moduleCount = 1;
		}
		else if (Array.isArray(loadModules)) {
			for (var i = 0; i < loadModules.length; i++) {
				if (loadModules[i] == null) continue;
				mods ??= [];
				if (typeof loadModules[i] === "function") {
					mods.push(loadModules[i]());
				}
				if (typeof loadModules[i] === "object") {
					//console.log(typeof loadModules[i], loadModules[i], loadModules);
					mods.push("kendo." + loadModules[i].toString());
				}
				else {
					mods.push(loadModules[i]);
				}
			}
		}
		else if (loadModules !== null && typeof loadModules === "object") {
			// TODO: fix
			mods = ["kendo." + (loadModules as any)?.toString()];
			moduleCount = 1;
		}

		let kendoAt = -1;
		for (i = mods.length - 1; i >= 0; i--) {
			if (mods[i].substring(0, 6) === "kendo.") {
				kendoAt = i;
				break;
			}
		}

		if (kendoAt > -1) {
			if (!this.didKendoModules) {
				this.didKendoModules = true;
				// Always load core first with `sync!` so that kendo culture can be set before other modules load
				this.extendModules(mods, null, ['sync!esm!/scripts/kendo/dist/kendo.core.js'], kendoAt);
				if (this.culture) {
					if (this.culture !== "en-US") {
						this.extendModules(mods, null, ['iife!/scripts/kendo/dist/cultures/kendo.culture.' + this.culture + '.js', 'iife!/scripts/kendo/dist/messages/kendo.messages.' + this.culture + '.js'], -1);
					}
				}
			}
			if (!this.isAdminPage) {
				const cssUrl = this.kendoThemePath ? this.kendoThemePath : "/client/bootstrap/themes/kendo.scss.css";
				this.extendModules(mods, null, ["css!" + cssUrl], -1);
			}
			for (i = 0; i < mods.length; i++) {
				if (mods[i].indexOf("kendo.") > -1 && mods[i].indexOf(".css") < 0 && mods[i].indexOf("!") < 0) {
					mods[i] = "esm!/scripts/kendo/dist/" + mods[i] + ".js";
				}
			}
		}

		// Aliases
		for (i = 0; i < mods.length; i++) {
			if (mods[i].startsWith("css!")) moduleCount--;
			switch (mods[i]) {
				case "jGrowl": mods[i] = "esm!/client/script/jquery.jgrowl.js"; break;
				case "bootstrap": mods[i] = BS; break;
				case "markdown-it": mods[i] = "esm!/scripts/markdown-it/dist/markdown-it.js"; break;
				case "/scripts/fabric/portal-config.js": mods[i] = "esm!/scripts/fabric/dist/portal-config.js"; break;
				//case "fabric": mods[i] = "/scripts/fabric/fabric.js"; break;
				//case "modelyn-fabric": mods[i] = "/scripts/fabric/modelyn-fabric.js"; break;
			}
		}

		//console.groupCollapsed("REQUIRE", mods, loadModules); console.trace(); console.groupEnd();
		const results: any[] = new Array(mods.length);

		const awaitModules: LoaderInfo[] = [];

		// Phase 1: Load all non-sync modules in parallel

		const doAwaits = async () => {
			// Sort by priority (lower number means higher priority)
			awaitModules.sort((a, b) => a.prio - b.prio);

			// Wait for each sync module to load sequentially before continuing
			for (let i = 0; i < awaitModules.length; i++) {
				if (awaitModules[i].sync) {
					await awaitModules[i].loader();
				}
			}

			// Start loading all non-sync modules in parallel and wait for them
			const toLoad = awaitModules.filter(p => !p.sync).map(p => p.loader());
			//console.error("BATCH:", ...toLoad);
			/*const ax =*/ await Promise.allSettled(toLoad);
			/*ax.forEach((result, i) => {
				if (result.status === 'rejected') {
					console.error(`Module ${i} failed:`, result.reason);
				}
			});*/
			//console.log("done!", ...toLoad);
		}

		mods.forEach((module, index) => {
			//if (module.startsWith('phase2!')) return;
			let sync = false;
			if (module.startsWith('sync!')) {
				sync = true;
				module = module.substring(5);
			}
			if (!module.includes('!')) {
				// load AMD as sync
				sync = true;
			}
			const loaderInfo: LoaderInfo = {
				prio: module.startsWith('css!') ? 5 : 10,
				sync: sync,
				name: module,
				loader: () => this.loadModuleAsync(module, index, results)
			};
			awaitModules.push(loaderInfo);
		});

		await doAwaits();

		/*
		mods.forEach((module, index) => {
			if (module.startsWith('phase2!')) {
				module = module.substring(7);
				const loaderInfo: LoaderInfo = {
					name: module,
					loader: () => this.loadModuleAsync(module, index, results)
				};
				awaitModules.push(loaderInfo);
			}
		});

		await doAwaits();
		*/

		if (kendoAt > -1 && !this.didKendoGlobal) {
			this.didKendoGlobal = true;

			if (this.culture !== "en-US") {
				//console.log("MAIN === ", this.culture);
				kendo.culture(this.culture);
				//console.log("IS == ", kendo.culture());
			}

			$(window).on("resize", () => {
				kendo.resize($(".mdln-kendo-wrap"), false);
			});
		}

		//console.trace(results);
		return moduleCount == 1 ? results[0] : results;
	}

	private static loadModuleAsync(module: string, index: number, results: any[]): Promise<void> {
		return this.loadSingleModule(module).then((mod) => {
			//console.log("DONE MODULE:", module);
			if (mod && mod.default) {
				results[index] = mod.default;
				return;
			}
			results[index] = mod;
		});
	}

	private static loadSingleModule(module: string): Promise<any> {
		if (!this.moduleCache.has(module)) {
			//console.log("LOADING MODULE:", module);
			if (module.startsWith('css!')) {
				this.moduleCache.set(module, this.loadCSS(this.addEtag(module.substring(4))));
			}
			else if (module.startsWith('esm!')) {
				this.moduleCache.set(module, import(this.addEtag(module.substring(4))));
			}
			else if (module.startsWith('iife!')) {
				this.moduleCache.set(module, this.loadIIFE(this.addEtag(module.substring(5))));
			}
			else {
				// defaults to AMD
				this.moduleCache.set(module, this.loadAMD(module));
			}
		}
		return this.moduleCache.get(module)!;
	}

	private static addEtag(url: string): string {
		// Skip external URLs
		if (url.indexOf("://") > -1) {
			return url;
		}

		let hasPar = url.indexOf("?") > 0;
		let hasEtag = hasPar && url.indexOf("etag=") > 0;
		let hasDebug = hasPar && (url.indexOf("?debug") > 0 || url.indexOf("&debug") > 0);

		let newUrl = url;

		if (!hasEtag) {
			newUrl += (hasPar ? "&" : "?") + "etag=" + (url.indexOf("kendo.") > -1 ? "__ETAG_KENDO_PLACEHOLDER__" : this.etag);
			hasPar = true;
		}

		if (this.isDebugMode && !hasDebug) {
			newUrl += (hasPar ? "&" : "?") + "debug";
		}

		return newUrl;
	}

	// CSS loader helper
	private static loadCSS(href: string): Promise<void> {
		return new Promise((resolve, reject) => {
			//console.debug("LOADING CSS", href);
			const link = document.createElement('link');
			link.rel = 'stylesheet';
			link.href = href;
			link.onload = () => resolve();
			link.onerror = reject;
			document.head.appendChild(link);
		});
	}

	// IIFE loader helper
	private static loadIIFE(href: string): Promise<void> {
		return new Promise((resolve, reject) => {
			//console.debug("LOADING IIFE", href);
			const script = document.createElement('script');
			script.src = href;
			script.onload = () => resolve();
			script.onerror = reject;
			document.head.appendChild(script);
		});
	}

	// AMD loader helper - isolated context to prevent global AMD pollution
	static requirejsPromise: Promise<void> | null = null;
	static originalDefine: any;

	private static async loadAMD(module: string): Promise<any> {
		console.trace("--- AMD ---", module);

		if (!this.requirejsPromise) {
			this.originalDefine = global.define;
			this.requirejsPromise = (async () => {
				await this.loadIIFE('/node_modules/requirejs/require.js');
				if (typeof require === 'undefined') {
					console.error("RequireJS not available");
					//reject(new Error('RequireJS not available'));
					return;
				}
				global.define('jquery', [], function () {
					return global.jQuery;
				});
				global.requirejs.config({
					baseUrl: "/scripts/",
					paths: {
						//"css": "/scripts/amd-css",
						//"async": "/scripts/amd-async",
						"fabric": "/scripts/fabric/fabric",
						"modelyn-fabric": "/scripts/fabric/modelyn-fabric",
					},
					skipDataMain: true,
					//waitSeconds: 30,
					urlArgs: (id, url) => this.addEtag(url).replace(url, ""),
				});
			})();
		}
		await this.requirejsPromise;
		return new Promise((resolve, reject) => {
			return global.requirejs([module], (loadedModule: any) => {
				console.log(global.define);
				resolve(loadedModule);
				console.log(global.define);
				global.define = this.originalDefine;
			}, reject);
		});
		/*
		return new Promise((resolve, reject) => {
			const originalDefine = global.define;
			
			// Dependency registry for shimmed modules
			const shimRegistry: { [key: string]: any } = {
				'jquery': global.jQuery,
				'jQuery': global.jQuery
			};			
			global.define = (deps: string[] | Function, factory?: Function) => {
				let moduleExports;
				if (typeof deps === 'function') {
					// No dependencies: define(factory)
					moduleExports = deps();
				} else if (Array.isArray(deps) && factory) {
					// With dependencies: define([deps], factory)
					// Resolve dependencies from shim registry
					const resolvedDeps = deps.map(dep => {
						if (shimRegistry.hasOwnProperty(dep)) {
							return shimRegistry[dep];
						}
						// Handle 'exports' and 'module' AMD special dependencies
						if (dep === 'exports') return {};
						if (dep === 'module') return { exports: {} };
						console.warn(`Dependency '${dep}' not found in shim registry`);
						return undefined;
					});
					// Call factory with resolved dependencies
					moduleExports = factory(...resolvedDeps);
				}
				// Restore original define and resolve
				global.define = originalDefine;
				resolve(moduleExports);
			};			
			global.define.amd = true;			
			this.loadIIFE(this.addEtag(module)).catch((err) => {
				global.define = originalDefine;
				reject(new Error(`Failed to load AMD module: ${module}`));
			});
		});
		*/
	}

	static modelynEditorXmlSchemas: any;
	private static _modelynEditorModule: any;

	static async requireEditor(ready: Function = null): Promise<ModelynEditor> {
		if (!this._modelynEditorModule) {
			this.require(["css!/scripts/modelyn-editor/min/modelyn-editor.css", "css!/scripts/modelyn-editor/min/monaco-editor.css"]);
			const { initializeEditorSettings } = await import(this.addEtag('../../scripts/modelyn-editor/config/editor-settings.js'));
			this.modelynEditorXmlSchemas = initializeEditorSettings();
			this._modelynEditorModule = await import(this.addEtag('../../scripts/modelyn-editor/min/modelyn-editor.js'));
		}

		const mdln = this._modelynEditorModule;
		const modelynEditor = mdln.ModelynEditor;

		let path = document.location.origin + "/cpoadmin";
		modelynEditor.setApiUrlRoot(path + "/api/");
		modelynEditor.setXmlSchemas(Modelyn.modelynEditorXmlSchemas);

		ready?.(mdln);

		return modelynEditor as any;
	}

	// -------------
	// Public
	// -------------
	static $(id) {
		let obj = document.getElementById(id);
		return obj;
	}

	static CreateDelegate(instance, method) {
		return function () {
			return method.apply(instance, arguments);
		}
	}

	static AddHandler(element, eventName, handler) {
		var browserHandler = handler;
		if (element.addEventListener)
			element.addEventListener(eventName, browserHandler, false);
		else if (element.attachEvent)
			element.attachEvent('on' + eventName, browserHandler);
	}

	static RemoveHandler(element, eventName, handler) {
		var browserHandler = handler;
		if (element.removeEventListener)
			element.removeEventListener(eventName, browserHandler, false);
		else if (element.detachEvent)
			element.detachEvent('on' + eventName, browserHandler);
	}

	static StopPropagation(ev) {
		ev.stopPropagation ? ev.stopPropagation() : ev.cancelBubble = true;
	}

	static PreventDefault(ev) {
		ev.preventDefault ? ev.preventDefault() : ev.returnValue = false;
		return false;
	}

	static CancelEvent(ev) {
		ev.stopPropagation ? ev.stopPropagation() : ev.cancelBubble = true;
		ev.preventDefault ? ev.preventDefault() : ev.returnValue = false;
		return false;
	}

	static IsPageValid(): boolean {
		if (typeof (global.ValidatorOnLoad) == "function")
			return global.Page_IsValid;
		return true;
	}

	static ScrollTo(elm) {
		if (!elm) elm = this;
		if (typeof (jQuery) !== "function") {
			if (elm.scrollIntoView)
				elm.scrollIntoView(true);
			return;
		}
		elm = $(elm);
		if (!elm.is(":visible"))
			return;
		var pos = elm.position();
		if (pos.top < $(window).scrollTop()) {
			$("html,body").animate({ scrollTop: pos.top });
		}
		else if (pos.top + elm.outerHeight() > $(window).scrollTop() + $(window).height()) {
			var top = pos.top + elm.outerHeight() - $(window).height() + 10;
			$("html,body").animate({ scrollTop: top });
		}
	}

	/** Add or remove query string parameters from a URL.
	 * @param params - Array of [key, value] pairs. Empty value removes the param.
	 * @param url - The URL to modify. If omitted, uses current location.
	 * @returns The modified URL string.
	 * @example
	 * urlParams([["type", ""], ["w", "300"]], "/image.mdln?type=old&w=100");
	 * // → "/image.mdln?w=300"  (type removed, w updated)
	 * @example
	 * urlParams([["page", "2"]]); // no url = navigates current page
	 */
	static urlParams(params: [string, string | null][], url?: string): string {
		url = url || window.location.href;
		const hashIndex = url.indexOf("#");
		const hash = hashIndex >= 0 ? url.substring(hashIndex) : "";
		const urlWithoutHash = hashIndex >= 0 ? url.substring(0, hashIndex) : url;
		const searchIndex = urlWithoutHash.indexOf("?");
		const base = searchIndex >= 0 ? urlWithoutHash.substring(0, searchIndex) : urlWithoutHash;
		const searchParams = new URLSearchParams(searchIndex >= 0 ? urlWithoutHash.substring(searchIndex + 1) : "");
		for (const [key, value] of params) {
			if (value)
				searchParams.set(key, value);
			else
				searchParams.delete(key);
		}
		const search = searchParams.toString();
		return base + (search ? "?" + search : "") + hash;
	}

	/** @deprecated Use urlAddParams instead */
	static UrlAddParam(key, value, url): string {
		const useCur = (!url);
		let newUrl = this.urlParams([[key, value || ""]], url);
		if (useCur) {
			document.location = newUrl;
			return null;
		}
		return newUrl;
	}

	static UrlGetParam(name: string, url: string = null): string | null {
		const urlObj = new URL(url || window.location.href);
		return urlObj.searchParams.get(name);
	}

	// Gets the invariant value of a given set of form fields values
	static getFieldValue(name: string): string | null {
		// Mirrors GatherVariables in URLParametersBase.cs — parses culture-formatted display values to native/invariant values
		// REF: DDK34KSD34 (CPO, JS and NAV)
		const stateElm = document.getElementById(name + "_CpoS") as HTMLInputElement;

		const optionValues = [
				...Array.from(document.getElementsByName(name)),
				...Array.from(document.querySelectorAll(`input[type="checkbox"][name^="${CSS.escape(name)}$"]`)),
			].filter((elm, index, items): elm is HTMLInputElement => elm instanceof HTMLInputElement && (elm.type === "radio" || elm.type === "checkbox") && elm.checked && items.indexOf(elm) === index)
				.map((elm) => elm.value?.trim())
				.filter((value): value is string => !!value);
		if (optionValues.length) return optionValues.join(",");

		const elm = document.getElementById(name) as HTMLInputElement;
		//console.log(name, stateElm, elm);
		const value = elm?.value?.trim();
		if (!value)
			return null;
		if (!stateElm?.value)
			return value;
		const stateValue = stateElm.value;
		const stateType = stateValue.split("|")[0];
		switch (stateType[0]) {
			case 'i': {
				const n = this.parseFloat(value, null);
				return n != null ? Math.trunc(n).toString() : null;
			}
			case 'd': {
				const n = this.parseFloat(value, null);
				return n != null ? n.toString() : null;
			}
			case 'h':
				return decodeURIComponent(value);
			case 't': {
				// tt = time only — bare time strings aren't valid Date input, prepend a dummy date
				const d = new Date(stateType === "tt" ? "1970-01-01T" + value : value);
				//console.log(d.getTime(), value, d);
				if (isNaN(d.getTime()))
					return null;
				switch (stateType) {
					case "td":
						return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
					case "tt":
						return String(d.getHours()).padStart(2, "0") + ":" + String(d.getMinutes()).padStart(2, "0") + ":" + String(d.getSeconds()).padStart(2, "0");
					case "tz":
						return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0") + "T" + String(d.getHours()).padStart(2, "0") + ":" + String(d.getMinutes()).padStart(2, "0") + ":" + String(d.getSeconds()).padStart(2, "0");
					default:
						return d.toISOString();
				}
			}
		}
		return value;
	}

	static buildFieldsUrl(names: string[], url: string = null): string {
		if (!url)
			url = window.location.href;
		for (const name of names) {
			const value = this.getFieldValue(name);
			url = this.urlParams([[name, value]], url);
		}
		return url;
	}

	static buildFieldsJson(names: string[]): Record<string, string> {
		const result: Record<string, string> = {};
		for (const name of names) {
			const value = this.getFieldValue(name);
			if (value != null)
				result[name] = value;
		}
		return result;
	}

	/// SAME AS: KKSADLK24234 (CPO, JS and NAV)
	static RenderKey(key, doUpper) {
		var InterCharFrom = "çáéíóúàèìòùäëïöüÿâêîôûñõðš";
		var InterCharTo = "caeiouaeiouaeiouyaeiounods";
		if (!key)
			return null;
		var renderKey = "";
		var upperNext = false;

		for (var index = 0; index < key.length; index++) {
			var ch = key[index].toLowerCase();
			switch (ch) {
				case ' ':
				case '.':
				case ',':
				case ':':
				case '+':
				case '!':
				case ')':
				case '(':
				case '-':
				case '\'':
					if (renderKey.length > 0 && renderKey[renderKey.length - 1] == '-')
						continue;
					renderKey += '-';
					if (doUpper)
						upperNext = true;
					break;
				case 'æ':
					renderKey += "ae";
					break;
				case 'ø':
					renderKey += "oe";
					break;
				case 'å':
					renderKey += "aa";
					break;
				case 'œ':
					renderKey += "oe";
					break;
				case 'ß':
					renderKey += "ss";
					break;

				default:
					var p;
					if ((p = InterCharFrom.indexOf(ch)) > -1) {
						renderKey += InterCharTo[p];
						break;
					}
					if (encodeURIComponent(ch).length > 1)
						break;
					if (upperNext) {
						renderKey += ch.ToString().ToUpperInvariant();
						upperNext = false;
					}
					else
						renderKey += ch;
					break;
			}
		}
		return renderKey.replace(/^[\s-]+|[\s-]+$/g, '');
	}

	static Notify(message: string, options: NotifyOptions = null): void {
		this.require(["jGrowl"], () => {
			const prefix = "jGrowl-cpo-";
			if (options && options.theme && options.theme.substring(0, prefix.length) !== prefix) {
				options.theme = "jGrowl-cpo-" + options.theme;
			}
			($ as any).jGrowl(message, options);
		});
	}

	static ValidateForm(validationGroup: string = ""): boolean {
		if (typeof (global.Page_ClientValidate) == 'function') {
			if (!global.Page_ClientValidate(validationGroup)) {
				global.Page_BlockSubmit = false;
				return false;
			}
		}
		return true;
	}

	static AlternateForm(method, url, validation, validationGroup): boolean {
		if (method || url) {
			if (validation) {
				if (typeof (global.Page_ClientValidate) == 'function') {
					if (!global.Page_ClientValidate(validationGroup)) {
						global.Page_BlockSubmit = false;
						return false;
					}
				}
			}
		}
		else {
			return true;
		}
		if (method)
			global.theForm.method = method;
		if (url)
			global.theForm.action = url;
		//this.RemoveAspxFields();
		return true;
	}

	static Reload() {
		document.location.assign(document.location.href);
	}

	public static showSpinner(show: boolean = true, options: SpinnerOptions = undefined): HTMLElement {
		options = options || {} as SpinnerOptions;
		const screen = !options.container;
		const container = screen ? $("body") : $(options.container as any) as unknown as JQuery<HTMLElement>;
		if (container.length === 0) {
			return null;
		}
		let mask = container.find(".mdln-spinner-modal");
		if (!show) {
			const timer = (container[0] as any).mdlnSpinTimer;
			if (timer) {
				window.clearTimeout(timer);
				(container[0] as any).mdlnSpinTimer = null;
			}
			if (this.submitSpinner) {
				window.clearTimeout(this.submitSpinner);
				this.submitSpinner = null;
			}
			mask?.remove();
			return;
		}
		if (mask && mask.length > 0) {
			return mask[0];
		}
		mask = $(`<div class="mdln-spinner-modal modal-backdrop fade show">` +
			(options.template || '<div class="spinner-border"><span role="status" aria-live="polite" class="visually-hidden">Please wait</span></div>') +
			'</div>');
		if (!screen) {
			if (!container[0].style.position) {
				container[0].style.position = 'relative';
			}
		}
		else {
			mask.css("position", "fixed");
		}
		mask.prependTo(container);
		if (options.timeout) {
			const timer = (container[0] as any).mdlnSpinTimer;
			if (timer) {
				window.clearTimeout(timer);
			}
			(container[0] as any).mdlnSpinTimer = setTimeout(() => { mask?.remove(); }, options.timeout);
		}
		return mask[0];
	};

	static isSubmitting: boolean;

	public static toggleButtons(disabled: boolean, button: JQuery<HTMLElement> | string | HTMLElement = undefined) {
		let $button: JQuery<HTMLElement> = null;
		if (button) {
			$button = $(button as any) as unknown as JQuery<HTMLElement>;
		}
		if (!disabled) {
			document.querySelectorAll(".mdln-post-disabled").forEach((elm: HTMLInputElement) => {
				//elm.disabled = false;
				elm.classList.remove("mdln-post-disabled");
				elm.removeChild(elm.querySelector(".mdln-post-protector-btn"));
			});
		}
		else {
			const handleElm = (elm: HTMLElement) => {
				//elm.disabled = true;
				elm.classList.add("mdln-post-disabled");
				elm.insertAdjacentHTML("afterbegin",
					`<span class="mdln-post-protector-btn">` +
					(this.postProtectorOptions.buttonTemplate || `<span class="spinner-border spinner-border-sm" aria-hidden="true"></span>&nbsp;`) +
					`</span>`
				);
			};
			if ($button?.length > 0) {
				$button.each((_, elm) => handleElm(elm));
			}
			else {
				document.querySelectorAll("button[data-mdln-pb]").forEach(handleElm);
			}
		}
	};

	private static postProtectorOptionsDefault: PostProtectorOptions = {
		timeout: 600
	}
	private static postProtectorOptions: PostProtectorOptions = this.postProtectorOptionsDefault;

	public static setPostProtector(options: PostProtectorOptions = null) {
		this.postProtectorOptions = options || this.postProtectorOptionsDefault;
	}


	static async getJson(url: string): Promise<any> {
		return this.fetchJson(url, { method: 'GET' });
	}

	static async postJson(url: string, body: any | FormData): Promise<any> {
		const options: RequestInit = { method: 'POST' };

		if (body instanceof FormData) {
			options.body = body;
			// Let browser set Content-Type with boundary for FormData
		} else {
			try {
				options.body = JSON.stringify(body);
				options.headers = { 'Content-Type': 'application/json' };
			} catch (ex) {
				alert('Invalid JSON data');
				throw new Error('postJson `body` must be either FormData or JSON-stringifiable', { cause: ex });
			}
		}

		return this.fetchJson(url, options);
	}

	private static async fetchJson(url: string, options: RequestInit): Promise<any> {
		options.headers = { ...options.headers, 'Accept': 'application/json' };
		const spinTimer = window.setTimeout(this.showSpinner, 2000);

		try {
			const response = await fetch(url, options);

			if (!response.ok) {
				const errorText = await response.text().catch(() => '');
				const message = this.isDebugMode
					? `Server error (${response.status}): ${errorText || response.statusText}`
					: 'There was a problem communicating to the server';

				alert(message);
				throw new Error(`HTTP ${response.status}: ${response.statusText}`);
			}

			const text = await response.text();
			return text ? JSON.parse(text) : null;
		}
		catch (ex) {
			if (!(ex instanceof Error && ex.message.startsWith('HTTP'))) {
				alert('Network error - unable to reach server');
			}
			throw ex;
		}
		finally {
			window.clearTimeout(spinTimer);
			this.showSpinner(false);
		}
	}

	static submitSpinner;

	static FormSubmit() {
		// likeness from WebForm.js
		try {
			this.isAjax = global.Sys?.WebForms?.PageRequestManager?.getInstance()?._postBackSettings?.async;
			//console.error("submit", this.isAjax, this.isSubmitting, this.postProtectorOptions);
			// HACK: see: FLOOZZ56
			// Important for conflict when hitting ENTER + having an onchange = double post
			if (this.isSubmitting) {
				console.debug("skipping submit dupe");
				return false;
			}

			const evCall = $.Event("submit-valid.mdln");
			$(global.theForm).triggerHandler(evCall);
			if (evCall.isDefaultPrevented()) {
				return false;
			}
			this.isSubmitting = true;
			const options = this.postProtectorOptions;

			const restorePostProtector = () => {
				if (options.button) {
					this.toggleButtons(false);
				}
				if (options.modal) {
					this.showSpinner(false);
				}
				this.isSubmitting = false;
				this.submitSpinner = null;
				if (options.once) {
					this.setPostProtector();
				}
			};

			const showSpinners = () => {
				if (!this.isSubmitting) return;
				if (options.button) {
					this.toggleButtons(true, document.getElementById(global.theForm.elements["__EVENTTARGET"]?.value));
				}
				if (options.modal && (!options.modalIf || options.modalIf())) {
					this.showSpinner(true, { template: options.modalTemplate });
				}
			}
			if (!options.wait) {
				showSpinners();
			}
			else {
				this.submitSpinner = window.setTimeout(showSpinners, options.wait);
			}

			this.replaceEvent(window, "pagehide", restorePostProtector);
			this.replaceEvent(document, "ajax-response-end.mdln", restorePostProtector);

			if (options.timeout > 0) {
				window.setTimeout(restorePostProtector, options.timeout);
			}

			/// See REF: flk43lk23gbhn
			if (!Modelyn.ignoreFocus) {
				const lastFocus = global.theForm.elements["__LASTFOCUS"];
				if ((typeof (lastFocus) != "undefined") && (lastFocus != null)) {
					if (lastFocus.value != null && lastFocus.value.length > 0)
						return true;
					if (typeof (document.activeElement) != "undefined") {
						const active = document.activeElement as any;
						if ((typeof (active.id) != "undefined") && (active.id != null) && (active.id.length > 0)) {
							var check = document.querySelectorAll("#" + CSS.escape(active.id));
							if (check.length > 1) {
								console.error("multi ids: " + active.id);
								return true;
							}
							lastFocus.value = active.id;
						}
						else if (typeof (active.name) != "undefined") {
							lastFocus.value = active.name;
						}
					}
				}
			}
		} catch (ex) {
			console.error(ex);
		}
		return true;
	}

	static replaceEvent(target: EventTarget, eventType: string, listener: EventListener, options?: EventListenerOptions): void {
		target.removeEventListener(eventType, listener, options);
		target.addEventListener(eventType, listener, options);
	}

	static OnEnterTab(ev) {
		if (ev.keyCode !== 13)
			return;
		const focusable = $(ev.srcElement).parents("form:eq(0)").find("input,a,select,button,textarea").filter(":visible:not([readonly]):enabled");
		const next = focusable.eq(focusable.index(ev.srcElement) + 1);
		if (next.length) {
			next.trigger("focus");
			ev.preventDefault();
			ev.stopPropagation();
			return false;
		}
		return true;
	}

	static OnEnterClick(ev: KeyboardEvent, obj: (HTMLElement | string)) {
		this.OnKeyClick(ev, obj, 13);
	}

	static OnKeyClick(ev: KeyboardEvent, obj: (HTMLElement | string), keyCode: number = 13) {
		if (typeof (obj) == "string") {
			obj = document.getElementById(obj as string);
		}
		if (!obj) {
			return;
		}
		const key = ev.keyCode;
		if (key !== keyCode || ev.shiftKey) {
			return;
		}
		ev.preventDefault ? ev.preventDefault() : ev.returnValue = false;
		ev.stopPropagation ? ev.stopPropagation() : ev.cancelBubble = true;
		Modelyn.FireEvent(obj, "click");
	}

	static FireEvent(obj, name) {
		if (typeof (obj) === "string") {
			obj = document.getElementById(obj);
		}
		if (!obj) {
			return null;
		}
		if (obj.tagName === "A") {
			if (obj.href.substring(0, 11) === "javascript:") {
				const code = decodeURI(obj.href.substring(11));
				window.setTimeout(code, 0);
				return true;
			}
		}
		if (!document.createEvent)
			return obj.fireEvent("on" + name);
		else {
			// IE9 and others
			const evt = document.createEvent("MouseEvents");
			evt.initMouseEvent(name, true, true, window, 0, 0, 0, 0, 0, false, false, false, false, 0, null);
			return !obj.dispatchEvent(evt);
		}
	}

	static ClearItems(id) {
		// Only supports RadComboBox for now
		var elm = global.$find(id);
		elm.clearItems();
		(elm as any).didLoad = false;
	}

	static CollectFormItems(parentId, skipUnderscores) {
		if (!jQuery) {
			alert("Missing jQuery");
			return;
		}
		let q: string;
		if (parentId && parentId.length > 0)
			q = "#" + parentId + " *";
		/*var elements = $(q || "form").map(function () {
			return this.elements ? jQuery.makeArray(this.elements) : this;
		}).filter(function () {
			return this.name && !this.disabled &&
					(this.checked || /select|textarea/i.test(this.nodeName) || /text|hidden|password|search/i.test(this.type));
		}).get();*/

		var elements = $(q || "form").filter(":input");

		var formValues = [];
		for (var fi = 0; fi < elements.length; fi++) {
			let inputElm = elements[fi] as HTMLInputElement;
			var name = inputElm.name;
			if (!name || (skipUnderscores && name.indexOf("_") > -1))
				continue;
			/*
			switch (name) {
			case "__VIEWSTATE":
			case "__EVENTTARGET":
			case "__EVENTARGUMENT":
			case "__SCROLLPOSITIONX":
			case "__SCROLLPOSITIONY":
			case "__PREVIOUSPAGE":
			case "__EVENTVALIDATION":
			continue;
			}*/
			var value = inputElm.value;
			//if (!value)
			//	continue;
			var type = inputElm.type;
			switch (type) {
				case "submit":
					continue;
				case "checkbox":
					if (!inputElm.checked)
						continue;
					else
						value = "true";
					break;
			}
			//s2 += name + "(" + type + "): " + value + "\r\n";
			formValues.push({ Key: name, Value: value });
		}
		//alert(s); alert(s2);
		return formValues;
	}

	static RemoveSystemFields(ignores: string[] = null) {
		let fields = [];
		for (let i = 0; i < global.theForm.elements.length; i++) {
			let field = global.theForm.elements[i] as HTMLInputElement;
			if (ignores && ignores.indexOf(field.name) > -1)
				continue;
			if (field.name.substring(0, 2) === "__" || field.name === "scriptManager_TSM") {
				fields.push(field.name);
			}
			else if (field.name.indexOf("_ClientState") > -1 || field.name.indexOf("_CpoS") > -1 /*|| field.name.indexOf("_CpoS") > -1*/) {
				fields.push(field.name);
			}
		}
		for (let i = 0; i < fields.length; i++) {
			this.RemoveField(fields[i]);
		}
		this.RemoveField("ScriptManager_HiddenField");
	}

	static RemoveField(name) {
		var obj = global.theForm.elements[name];
		if (!obj)
			return;
		var parent = obj.parentNode;
		parent.removeChild(obj);
		global.theForm.elements[name] = null;
	}

	static NumericInput(ev, groupAcsii, decAcsii) {
		if (!groupAcsii) groupAcsii = 44;
		if (!decAcsii) decAcsii = 46;
		const key = ev.which != undefined ? ev.which : ev.keyCode;
		if (key === groupAcsii || key === 32) {
			//if (ev.target && ev.target.value != undefined && ev.target.value.length == 0)
			//	return this.PreventDefault(ev);
			return true;
		}
		if ((key >= 48 && key <= 57) || key === decAcsii || key === 8 || key === 9 || key === 0 || key === 45 || key === 13)
			return true;
		return this.PreventDefault(ev);
	}

	static parseFloat(str: string | number | boolean, defaultValue: number = 0): number {
		if (typeof (str) === "number")
			return str as number;
		if (typeof (str) === "boolean")
			return str ? 1 : 0;
		if (str === "")
			return defaultValue;
		const splits = str.split(new RegExp("[" + (this.decimalSeparator || ".") + "]"));
		if (splits.length > 2)
			return defaultValue;
		// heyja norway!
		const sepChar = this.thousandSeparator.charCodeAt(0);
		for (let i = 0; i < splits.length; i++) {
			splits[i] = splits[i].replace(new RegExp("[" + (
				(sepChar === 160 || sepChar === 32) && this.decimalSeparator !== "." ? "\s\." : (this.thousandSeparator || ".")) + "]", "g"), "");
		}
		const n = parseFloat(splits.join("."));
		if (isNaN(n))
			return defaultValue;
		return n;
	}

	static ScrollbarSize() {
		var div = $('<div style="width:50px;height:50px;overflow:hidden;position:absolute;top:-200px;left:-200px;"><div style="height:100px;" /></div>');
		$(document.body).append(div);
		var w1 = $('div', div).innerWidth();
		div.css('overflow-y', 'auto');
		var w2 = $('div', div).innerWidth();
		$(div).remove();
		return (w1 - w2);
	}

	static CallSys(callId, arg, noAjax, targetId) {
		if (arg) {
			let objArg = document.getElementById('__CPOARG') as HTMLInputElement;
			if (!objArg) {
				alert("No actions registered");
				return;
			}
			objArg.value = arg;
		}
		if (!noAjax && global.$find) {
			var ajaxManager = global.$find("MdlnAjaxManager") as any;
			if (ajaxManager) {
				if (targetId)
					ajaxManager.ajaxRequestWithTarget(targetId, callId);
				else
					ajaxManager.ajaxRequest(callId);
				return;
			}
		}
		global.__doPostBack("cpoSysEvent", callId);
	}

	static keepAliveThread: number;

	static StopKeepAlive(interval, callback) {
		if (this.keepAliveThread)
			window.clearInterval(this.keepAliveThread);
		this.keepAliveThread = null;
	}

	// Modelyn.StartKeepAlive(5, function(httpReq) { alert("Server status "+ httpReq.status +" said: "+ httpReq.responseText); });
	static StartKeepAlive(interval, callback) {
		if (this.keepAliveThread)
			return;
		if (!interval)
			interval = 60 * 5;
		this.keepAliveThread = window.setInterval(this.CreateDelegate(this, function () {
			this.ServerTalk("ping", callback);
		}), interval * 1000);
	}

	static reqCounter: number = 0;

	static ServerTalk(cmd, callback) {
		this.reqCounter++;
		let httpReq = new XMLHttpRequest();
		httpReq.open("GET", "/mdln-api/services/rest/Talk?rc=" + this.reqCounter + "&cmd=" + cmd, true);
		httpReq.onreadystatechange = this.CreateDelegate(this, () => {
			if (httpReq.readyState === 4) {
				if (callback) {
					var retVal = callback(httpReq);
					if (retVal != undefined && !retVal)
						window.clearInterval(this.keepAliveThread);
				}
			}
		});
		httpReq.send(null);
	}

	static Serialize(obj) {
		// TODO: test for native: window.JSON
		// TODO: Check for ajax avail and if obj is HTML (jquery = .serialize()).
		//return Sys.Serialization.JavaScriptSerializer.serialize(obj);
		return JSON.stringify(obj);
	}

	static DeSerialize(stringVal) {
		// TODO: test for native: window.JSON
		//return Sys.Serialization.JavaScriptSerializer.deserialize(stringVal);
		return JSON.parse(stringVal);
	}

	static InsertAtCursor(elm: HTMLInputElement, value) {
		if (elm.selectionStart || elm.selectionStart === 0) {
			let startPos = elm.selectionStart;
			let endPos = elm.selectionEnd;
			let restoreTop = elm.scrollTop;

			elm.value = elm.value.substring(0, startPos) + value + elm.value.substring(endPos, elm.value.length);

			elm.selectionStart = startPos + value.length;
			elm.selectionEnd = startPos + value.length;

			if (restoreTop > 0)
				elm.scrollTop = restoreTop;
		}
	}

	static DebounceEvent(sender: any, func: Function, wait: number, immediate: boolean): boolean {
		if (!sender) return false;
		if (sender.debounceEvent) {
			sender.debounceEvent();
			return true;
		}
		sender.debounceEvent = this.Debounce(func, wait, immediate, sender);
		sender.debounceEvent();
		return true;
	}

	static Debounce(func: Function, wait: number = 250, immediate: boolean = false, scope: any = null): Function {
		let timeout: number;
		return function () {
			const event = global.event || new Event("dummy"); // NOTE: prevent Sys$WebForms$PageRequestManager$_doPostBack error in FF with "use strict"
			var context = scope || this, args = arguments;
			if (timeout) {
				clearTimeout(timeout);
			}
			if (immediate) {
				immediate = false;
				global.event = global.event || event;
				func.apply(context, args);
				return;
			}
			timeout = window.setTimeout(() => {
				timeout = null;
				global.event = global.event || event;
				func.apply(context, args);
			}, wait || 250);
		};
	}

	static DebouncePostBack(sender: any, wait: number = 400) {
		if (!sender) return true;
		if (!sender.currentApb) {
			sender.currentApb = sender.onchange;
			sender.onchange = null;
		}
		if (!sender.currentApb)
			return true;
		if (sender.debounceEvent) {
			sender.debounceEvent();
			return true;
		}
		sender.debounceEvent = this.Debounce(() => {
			try {
				sender.currentApb();
			} catch (ex) { }
		}, wait);
		sender.debounceEvent();
	}


	static ThrottleEvent(sender: any, func: Function, threshold: number): boolean {
		if (!sender) return false;
		if (sender.throttleEvent) {
			sender.throttleEvent();
			return true;
		}
		sender.throttleEvent = this.Throttle(func, threshold, sender);
		sender.throttleEvent();
		return true;
	}

	static Throttle(fn: Function, threshold: number = 250, immediate: boolean = false, scope: any = null): Function {
		let last, deferTimer;
		return function () {
			const event = global.event || new Event("dummy"); // NOTE: prevent Sys$WebForms$PageRequestManager$_doPostBack error in FF with "use strict"
			var context = scope || this;
			var now = +new Date, args = arguments;
			if (last && now < last + threshold) {
				clearTimeout(deferTimer);
				deferTimer = setTimeout(() => {
					last = now;
					if (window.requestAnimationFrame) {
						window.requestAnimationFrame(() => {
							global.event = global.event || event;
							fn.apply(context, args);
						});
					}
					else {
						fn.apply(context, args);
					}
				}, threshold);
			} else {
				last = now;
				global.event = global.event || event;
				fn.apply(context, args);
			}
		};
	}

	static HtmlEncode(html: string) {
		if (typeof (html) !== "string") return html;
		return html?.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
	}

	static StringBreak(value: string, encode: boolean = true) {
		if (typeof (value) !== "string") return value;
		return (encode ? this.HtmlEncode(value) : value).replace(/([\\\/].)/g, '&#8203;$1');
	}

	static GetQueryFromIdRefs(idRefs) {
		if (!idRefs)
			return null;
		return "#" + idRefs.split(/[,\s]+/g).join(", #");
	}

	/**
	 * DEPRECATED!
	 * @private
	 * @deprecated Deprecated
	 */
	static DownloadFile(href: string): void { this.noSpinnerNav(href); }

	static noSpinnerNav(href: string): void {
		const link = document.createElement("a");
		link.href = href;
		link.download = "";
		link.click();
		this.downloadDone();
	}

	static downloadDone(tms: number = 1000): void {
		setTimeout(() => {
			this.isSubmitting = false;
			this.showSpinner(false);
		}, tms);
	}

	// -------------
	// System
	// -------------
	static onDragOutfile(ev, fileDetails) {
		if (!ev || !ev.dataTransfer)
			return;
		var elm = ev.target;

		fileDetails = fileDetails
			|| (elm.dataset && elm.dataset.downloadurl ? elm.dataset.downloadurl : null)
			|| elm.getAttribute("data-downloadurl");

		//if (!fileDetails) console.log("oooops!");
		ev.dataTransfer.setData("DownloadURL", fileDetails);
	}

	static setField(name, value) {
		var obj = document.getElementById(name) as HTMLInputElement;
		if (!obj) {
			console.log(name + " not found");
			return;
		}
		obj.value = value;
	}

	static navPage(group, relative, index, sort) {
		let obj = document.getElementById(group + "_NDN") as HTMLInputElement;
		if (obj) {
			obj.value = "1";
		}
		obj = document.getElementById("index" + group) as HTMLInputElement;
		if (!obj)
			return;
		if (relative) {
			let pageNumber = parseInt("0" + obj.value, 10);
			if (pageNumber == 0)
				pageNumber = 1;
			if (pageNumber < 0)
				return;
			obj.value = pageNumber + index;
		}
		else {
			obj.value = index;
		}
		if (sort) {
			this.setField("sort" + group, sort);
		}
		obj = document.getElementById("__SCROLLPOSITIONX") as HTMLInputElement;
		if (obj) obj.remove();
		obj = document.getElementById("__SCROLLPOSITIONY") as HTMLInputElement;
		if (obj) obj.remove();
		//__doPostBack(null, null);
	}

	static clearValidation(): boolean {
		if (!global.ValidationSummaryOnSubmit)
			return;
		global.ValidationSummaryOnSubmit("...");
		global.Page_BlockSubmit = false;
		if (global.Page_Validators) {
			for (var i = 0; i < global.Page_Validators.length; i++) {
				let val = global.Page_Validators[i];
				val.isvalid = true;
				global.ValidatorUpdateDisplay(val);
			}
		}
	}

	static handleCommand(ev: Event, $ctrl: JQuery<HTMLElement>, validation: string): boolean {
		global.event = ev; // NOTE: prevent Sys$WebForms$PageRequestManager$_doPostBack error in FF with "use strict"

		let id = $ctrl[0].id;
		//console.log("-- modelyn click @1 #" + id);

		if (ev.defaultPrevented) {
			//if (ev.isDefaultPrevented()) {
			//console.log("-- modelyn skip click #" + id);
			return false;
		}

		this.clearValidation();

		if (validation !== undefined && typeof (global.Page_ClientValidate) == "function") {
			let validationResult = global.Page_ClientValidate(validation);
			if (!validationResult) {
				global.Page_BlockSubmit = false;
				return false;
			}
		}

		let confirmMsg = $ctrl.data("mdln-confirm") as string;
		if (confirmMsg) {
			if (!window.confirm(confirmMsg)) {
				return false;
			}
		}

		var clickValidEv = $.Event("click-valid.mdln", {
			validation: validation
		});
		$ctrl.triggerHandler(clickValidEv);

		if (clickValidEv.isDefaultPrevented()) {
			//console.log("-- modelyn skip click after validation #" + id);
			return false;
		}

		//console.log("-- modelyn click @2 #" + id);
		let mdlnEventTargetName = "__EVENTTARGET-MODELYN";
		let evElm = document.getElementById(mdlnEventTargetName) as HTMLInputElement;
		if (!evElm) {
			evElm = document.createElement("input");
			evElm.setAttribute("type", "hidden");
			evElm.setAttribute("id", mdlnEventTargetName);
			evElm.setAttribute("name", mdlnEventTargetName);
			global.theForm.appendChild(evElm);
		}
		evElm.value = id;

		const value = $ctrl.val() as string;
		if (value) {
			// TODO: Not sure this is needed? Was it for button type="button" (vs. not submit)
			mdlnEventTargetName = "__EVENTVALUE-MODELYN";
			evElm = document.getElementById(mdlnEventTargetName) as HTMLInputElement;
			if (!evElm) {
				evElm = document.createElement("input");
				evElm.setAttribute("type", "hidden");
				evElm.setAttribute("id", mdlnEventTargetName);
				evElm.setAttribute("name", mdlnEventTargetName);
				global.theForm.appendChild(evElm);
			}
			evElm.value = value;
		}

		if (typeof (global.Sys) != "object" || !global.Sys.WebForms)
			return true;

		/// See SDKSDK344 - handle `<button><span>click on span ...` - this elm. vs. ctrl (button)
		//let ajaxRequestManager = Sys.WebForms.PageRequestManager.getInstance();
		//(ajaxRequestManager as any)._onFormElementClick({
		//	target: ctrl,
		//	offsetX: ev.offsetX,
		//	offsetY: ev.offsetY
		//});
		//console.log((ajaxRequestManager as any)._additionalInput);

		//console.log($ctrl.data("mdln-pb"));
		let skipAjax = $ctrl.data("mdln-pb") as string === "ff";
		if (skipAjax) {
			/// Ensures that the control post a full post back and not a partial ajax call
			var list = (global.Sys.WebForms.PageRequestManager as any)._instance._postBackControlClientIDs;
			if (!global.Array.contains(list, id)) {
				list.push(id);
			}
		}

		return true;
	}

	public static config(cfg: JSON): void {
		Object.assign(this, cfg);
	}

	static doInit(): void {
		//console.log("... doInit!");
		/// See SDKSDK344
		//if (global.Sys) {
		//	let ajaxRequestManager = Sys.WebForms.PageRequestManager.getInstance();
		//	Sys.UI.DomEvent.removeHandler(global.theForm, "click", (ajaxRequestManager as any)._onFormElementClickHandler);
		//}

		// HACK FireFox event
		//try {
		//	if (navigator.userAgent.toLowerCase().indexOf("firefox") > -1) {
		//		document.documentElement.addEventListener("click", (event) => {
		//			global.event = event;
		//		}, true);
		//	}
		//}
		//catch (ex) {
		//	// ignore
		//}

		let that = this;

		$("*[data-mdln-pb]").each((i, ctrl) => {
			let $ctrl = $(ctrl);
			if ($ctrl.data("did-init-mdln"))
				return;
			$ctrl.data("did-init-mdln", 1);

			let asSubmit = ctrl.tagName === "BUTTON" && (ctrl as HTMLButtonElement).type === "submit" ||
				ctrl.tagName === "INPUT" && (ctrl as HTMLInputElement).type === "submit";

			const validation = $ctrl.data("mdln-val") as string;
			const name = $ctrl.data("mdln-name") || (ctrl as HTMLButtonElement).name || ctrl.id;
			const url = $ctrl.data("mdln-url") as string;

			// Attach a capturing phase event listener
			ctrl.addEventListener('click', () => {
				// Define the bubbling phase handler
				const bubblingHandler = (ev: Event) => {
					// Remove the bubbling handler after execution
					ctrl.removeEventListener('click', bubblingHandler);

					// Clear validation and check if default action hasn't been prevented
					if (!that.handleCommand(ev, $ctrl, validation)) {
						ev.preventDefault();
						return false;
					}
					if (validation == undefined && !url) {
						if (!asSubmit) {
							global.__doPostBack(name, "");
						}
					}
					else {
						global.WebForm_DoPostBackWithOptions({
							eventTarget: name,
							eventArgument: "",
							//trackFocus: false,
							validation: validation != undefined,
							validationGroup: validation,
							actionUrl: url,
							clientSubmit: !asSubmit
						});
					}
				}
				// Attach the bubbling phase handler
				ctrl.addEventListener('click', bubblingHandler);

			}, true); // The 'true' ensures this is in the capturing phase

		});
	}

	static Dequeue(id, timeout) {
		var dqf = () => {
			$.ajax({ url: "/mdln-api/services/rest/Dequeue?id=" + id });
		};
		if (!timeout)
			dqf();
		else
			window.setTimeout(dqf, timeout);
	}


	static browser: any;

	static GetBrowser() {
		if (this.browser)
			return this.browser;
		var match = [],
			browserRxs = {
				webkit: /(chrome)[ \/]([\w.]+)/i,
				safari: /(webkit)[ \/]([\w.]+)/i,
				opera: /(opera)(?:.*version|)[ \/]([\w.]+)/i,
				//msie: /(msie) ([\w.]+)/i,
				msie: /(trident)(?:.*? rv:([\w.]+)|)/i,
				edge: /(edge)(?:.*? rv:([\w.]+)|)/i,
				mozilla: /(mozilla)(?:.*? rv:([\w.]+)|)/i
			};
		for (var agent in browserRxs) {
			if (browserRxs.hasOwnProperty(agent)) {
				match = navigator.userAgent.match(browserRxs[agent]);
				if (match) {
					this.browser = {};
					this.browser[agent] = true;
					this.browser[match[1].toLowerCase()] = true;
					this.browser.version = match[2];
					break;
				}
			}
		}
		return this.browser;
	}

	static toRgba(color: string, alpha = 1): string {
		const canvas = document.createElement('canvas');
		const ctx = canvas.getContext('2d')!;
		ctx.fillStyle = color;
		ctx.fillRect(0, 0, 1, 1);
		const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
		return `rgba(${r}, ${g}, ${b}, ${alpha})`;
	}

	static makeBlankImage(options: BlankImageOptions = {}): string {
		let { width = "3000", height = "3000", color = "rgba(0,0,0,0)", opacity = null } = options;
		if (opacity && color) color = this.toRgba(color, opacity);
		const fill = `<rect width="100%" height="100%" fill="${color}" />`;
		const svg = `<svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">${fill}</svg>`;
		return `data:image/svg+xml,${encodeURIComponent(svg)}`;
	}

	//private static resImgs: IResImgInfo[key : string];
	private static resImages: { [key: string]: ResImgInfo; }

	static getImgSrcScaled(src: string, elm: HTMLElement, dev: boolean) {
		if (!elm.parentElement) {
			return src;
		}

		const ratio = Math.max(window.devicePixelRatio || 1, 1);
		let box: DOMRect;

		const fixedSize = elm.style.width;
		if (fixedSize && fixedSize.indexOf("px") > -1) {
			box = elm.getBoundingClientRect();
		}
		else {
			// Grow the image to max first to avoid multiple size animation
			//elm.style.display = "";
			//const orgWidth = elm.style.width;
			//elm.style.width = "auto";
			//elm.style.maxWidth = "100%";
			box = elm.parentElement.getBoundingClientRect();
			//elm.style.width = orgWidth;

			//console.log(44, orgWidth);
			//console.error(box, src, elm.parentElement);
		}

		if (box.height === 0 || box.width === 0) {
			return src;
		}

		let w = box.width * ratio, h = box.height * ratio;

		if (!src || (dev && src === "null")) {
			if (!dev) return "404.png";
			elm.style.display = "none";
			return this.makeBlankImage({ width: w, height: h });
		}

		if (src.indexOf(".gif") > 0) {
			return src;
		}
		if (w === 0 && h === 0 && !global.isPle) {
			return this.makeBlankImage();
		}
		const roundUp = (value: number) => {
			const step =
				value < 100 ? 20 :
					value < 500 ? 50 :
						value < 1000 ? 100 :
							200;
			return Math.floor(value / step) * step + step;
		};

		const cw = roundUp(w);
		const ch = roundUp(h);

		/*
		const lastW = elm["cl-last-w"];
		if (lastW && lastW >= cw) {
			return null;
		}
		elm["cl-last-w"] = cw;
		*/
		const newSrc = this.urlParams([
			["type", "GenerateThumb"],
			["scm", "MinBoth"],
			["w", cw.toString()],
			["h", ch.toString()]
		], src);

		if (!this.resImages) {
			this.resImages = {};
		}
		const resImg = Modelyn.resImages[src];
		if (resImg && (cw <= resImg.w && ch <= resImg.h)) {
			//if (dev) console.info(`found bigger! ${src} | ${cw}|${ch}  vs. ${resImg.src}`);
			return resImg.src;
		}
		else {
			Modelyn.resImages[src] = { w: cw, h: ch, src: newSrc };
		}
		return newSrc;
	}

	static async loadMedia(target: string | HTMLImageElement | HTMLVideoElement, options: LoadMediaOptionsSingle = {}): Promise<void> {
		let { src, skeleton = true } = options as LoadMediaOptionsSingle;

		//src: string, dev: boolean = false
		const elm = (typeof (target) === "string" ? document.querySelector(target as string) : target) as HTMLImageElement | HTMLVideoElement;
		if (!elm) {
			console.error("Responsive Image not found: " + target);
			return;
		}
		const isVideo = elm instanceof HTMLVideoElement;

		if (elm.tagName === "MODEL-VIEWER") {
			return;
		}

		let currentSrc = this.UrlStripDomain(elm.src);

		if (elm?.dataset?.src) {
			src = elm.dataset.src;
		}

		if (elm?.dataset?.etag) {
			src = src + (!src.match(/\?/g) ? "?" : "&") + "etag=" + elm.dataset.etag;
		}

		src ??= currentSrc;
		if (!src) {
			console.error("Invalid load img", src, elm, options);
			return;
		}

		if (src.indexOf("{:") > -1) {
			if (elm.src !== currentSrc) elm.src = "/client/img/photo/sample-square.jpg";
			return;
		}
		if (src.startsWith("mdln-avatar:")) {
			// NOTE: this must use string template to avoid import() issues in rollup
			const mod = await import(this.addEtag('../../node_modules/@multiavatar/multiavatar/dist/esm/index.js'));
			const svg = mod.default(src.substring(12))
			elm.src = `data:image/svg+xml,${encodeURIComponent(svg)}`;
			return;
		}

		const onLoad = () => {
			elm.classList.remove("mdln-img__skeleton");
		}

		if (skeleton) {
			elm.classList.add("mdln-img__skeleton");
			elm.addEventListener('load', (ev) => {
				if (elm.src.startsWith("data:")) {
					elm.addEventListener('load', (ev) => onLoad, { once: true });
					return;
				}
				onLoad();
			}, { once: true });
		}

		const elmA = elm as any;

		const set = (first: boolean) => {
			//console.log("resize", elm);
			if (!first && elmA.inResizer) {
				/// avoid "endless" resizing
				return;
			}
			if (!first) elmA.inResizer = true;

			if (isVideo) {
				const video = elm as HTMLVideoElement;
				//console.log("VID:", first, src, video);
				if (currentSrc !== src) {
					elm.src = src;
				}
				if (video.autoplay && video.paused && video.parentElement) {
					//console.info("PLAY");
					video.play();
				}
			}
			else {
				const url = this.getImgSrcScaled(src, elm, options?.dev);
				if (url && currentSrc !== url) {
					//console.error("IMG SRC: " + currentSrc, url, elm);
					elm.src = currentSrc = url;
				}
			}
			setTimeout(() => elmA.inResizer = false, 110);
		}

		if (elmA.mdlnSrc) {
			if (elmA.mdlnRecalc) {
				//elmA.mdlnRecalc();
				set(true);
			}
			return;
		}
		elmA.mdlnSrc = src;

		set(true);

		if (!isVideo) {
			const debounce = this.Debounce(() => {
				set(false);
			}, 100);
			elmA.mdlnRecalc = debounce;

			const resizeObserver = new ResizeObserver(entries => {
				debounce();
			});
			resizeObserver.observe(elm.parentElement);
		}
	}

	private static _loadMediaAllPending = false;

	static loadMediaAll(options: LoadMediaOptions): void {
		if (this._loadMediaAllPending) return;
		this._loadMediaAllPending = true;

		requestAnimationFrame(() => {
			this._loadMediaAllPending = false;

			const mediaElements = document.querySelectorAll<HTMLImageElement | HTMLVideoElement>('.mdln-img__load');
			mediaElements.forEach((element) => {
				const elm = element as HTMLImageElement | HTMLVideoElement;
				elm.classList.remove("mdln-img__load");
				// getImgSrcScaled
				this.loadMedia(elm, { ...options, src: elm.getAttribute('data-src') || elm.src });
			});
		});
	}

	/*
	static ResponsiveImgSetBg(target: string, src: string, dev: boolean = false): void {
		const elm = document.querySelector(target) as HTMLElement;
		if (!elm) return;

		if (global.supportsCssVar === undefined) {
			global.supportsCssVar = window.CSS && CSS.supports("color", "var(--tester)");
		}
		const url = this.getImgSrcScaled(src, elm, dev);
		if (global.supportsCssVar) {
			elm.style.cssText += "; --mdln-cl-bg: url('" + url + "')";
		}
		else {
			elm.style.backgroundImage = "url('" + url + "')";
		}
	}
	*/
	static parallaxMedia(target: string | HTMLElement, options: ParallaxOptions): void {
		options = options || {} as ParallaxOptions;
		const elm = (typeof (target) === "string" ? document.querySelector(target as string) : target) as HTMLElement;
		if (!elm) return;
		const orgImage = (typeof (options.media) === "string" ? elm.querySelector(options.media as string) : options.media) as HTMLImageElement | HTMLVideoElement;
		if (!orgImage) return;

		Modelyn.require(["esm!/node_modules/jarallax/dist/jarallax.esm.js"], (jarallax) => {
			jarallax.jarallax(elm, {
				type: options.type,
				imgElement: orgImage,
				speed: options.speed ?? 0.5,
				imgPosition: options.focal,
				keepImg: global.isPle,
				zIndex: 0,
				onInit: function () {
					if (orgImage.parentElement.parentElement.clientHeight < 5) {
						this.orgImg = orgImage;
						this.orgImg.style.visibility = "hidden";
					}
					this.$item.jarallax = this;
					Modelyn.loadMedia(this.image.$item, { src: options.src || this.image.$item.src });
				},
				onDestroy: function () {
					if (this.orgImg) this.orgImg.style.visibility = "";
				}
			});
		});
	}

	static AnimIn(prefix: string = "cl-v-anim-in--", newPrefix: string = "go-") {
		const elements = document.querySelectorAll(`*[class*=${prefix}]`);
		const obs = new IntersectionObserver(entries => {
			entries.forEach(entry => {
				if (entry.intersectionRatio > 0) {
					//entry.target.style.visibility = "";
					entry.target.classList.forEach((name) => {
						//if (name.startsWith(prefix)) {
						if (name.substring(0, prefix.length) === prefix) {
							Modelyn.addAnimClass(entry.target as HTMLElement, newPrefix + name.substring(prefix.length), false);
						}
					});
				}
			});
		} /*, { threshold: 0.4 } */);
		elements.forEach(element => {
			//element.style.visibility = "hidden";
			obs.observe(element);
		});
	}

	private static addAnimClass(elm: HTMLElement, className: string, remove: boolean, callback: () => void = null): void {
		const listen = () => {
			(elm as any).mdlnInTransition = true;
			elm.addEventListener("transitionend", (ev) => {
				const elements = elm.querySelectorAll("img");
				elements.forEach(img => {
					(elm as any).mdlnInTransition = false;
					if (callback) callback();
					// responsinve image
					if ((img as any).mdlnRecalc) (img as any).mdlnRecalc();
				});

			}, { once: true });
		}

		if (elm.classList.contains(className)) {
			if (remove) {
				listen();
				elm.classList.remove(className);
			}
			return;
		}

		if (!(elm as any).mdlnInTransition) {
			const offset = $(elm).offset();
			elm.style.setProperty("--mdln-offset-left", offset.left.toString());
			elm.style.setProperty("--mdln-offset-top", offset.top.toString());
			//console.log("offset.left", offset.left);

			elm.style.setProperty("--mdln-width", elm.offsetWidth.toString());
			elm.style.setProperty("--mdln-height", elm.offsetWidth.toString());

			const box = elm.getBoundingClientRect();
			elm.style.setProperty("--mdln-bb-left", box.left.toString());
			elm.style.setProperty("--mdln-bb-top", box.top.toString());
		}

		listen();
		elm.classList.add(className);
	}

	static AnimOver(prefix: string = "cl-v-anim-over--", newPrefix: string = "go-") {
		const elements = document.querySelectorAll(`*[class*=${prefix}]`);
		elements.forEach(entry => {
			const evTarget = entry.classList.contains("mdln-sys-event-parent") ? entry.parentElement : entry;
			evTarget.addEventListener("mouseenter", () => {
				entry.classList.forEach((name) => {
					if (name.substring(0, prefix.length) === prefix) {
						Modelyn.addAnimClass(entry as HTMLElement, newPrefix + name.substring(prefix.length), false);
					}
				});
			});

			(evTarget.parentElement.classList.contains("cl-block") ? evTarget.parentElement : evTarget).addEventListener("mouseleave", () => {
				entry.classList.forEach((name) => {
					if (name.substring(0, prefix.length) === prefix) {
						Modelyn.addAnimClass(entry as HTMLElement, newPrefix + name.substring(prefix.length), true);
					}
				});
			});
		});
	}

	static AlignGrid(hostsQ: string = "cl-v-block--align-cells-v", targetsQ: string = "cl-content") {
		const doWork = () => {
			const elements = document.querySelectorAll(`div.${hostsQ}`);
			const rows: HTMLElement[][] = [];
			elements.forEach((entry: HTMLElement) => {
				const targetParent = entry.querySelector(`div.${targetsQ}`) as HTMLElement;
				const row = Math.round(entry.getBoundingClientRect().top);
				if (!rows[row]) {
					rows[row] = [];
				}
				rows[row].push(targetParent);
			});

			for (let key in rows) {
				if (!Object.prototype.hasOwnProperty.call(rows, key))
					continue;
				const row = rows[key];
				if (!row || row.length <= 1) {
					if (row) {
						for (let target of row) {
							for (let i = 0; i < target.children.length; i++) {
								const child = target.children[i] as HTMLElement;
								child.style.minHeight = "";
							}
						}
					}
				}
				let childIndex = 0;
				while (childIndex >= 0) {
					let maxHeight = 0;
					for (let targetParent of row) {
						if (childIndex >= targetParent.children.length) {
							childIndex = -1;
							break;
						}
						const child = targetParent.children[childIndex] as HTMLElement;
						child.style.minHeight = "";
						maxHeight = Math.max(child.clientHeight, maxHeight);
					}
					if (childIndex < 0) {
						break;
					}
					for (let targetParent of row) {
						const child = targetParent.children[childIndex] as HTMLElement;
						child.style.minHeight = maxHeight + "px";
					}
					childIndex++;
				}
			}
		}
		doWork();
		if (!global.isPle) window.addEventListener("resize", doWork);
	}

	static BlockLink(blockQ: string = ".cl-link") {
		if (CSS.supports('width', 'stretch') || CSS.supports('width', '-webkit-fill-available')) return;
		$(blockQ).on('click', (ev: JQuery.ClickEvent) => $(ev.currentTarget).find('a')[0]?.click());
		$(blockQ).on('mousedown', (ev: JQuery.MouseDownEvent) => {
			if (ev.which !== 2) return;
			ev.preventDefault();
			const link = $(ev.currentTarget).find('a')[0];
			if (link) window.open(link.href, '_blank');
		});
	}

	static CollageInit() {
		const elements = document.querySelectorAll(".cl-v-txt--scroll,.cl-v-txt--scroll-vis");
		elements.forEach((entry: HTMLElement) => {
			//console.log(entry);
			//const elm = entry.firstElementChild as HTMLElement;
			const elm = entry;
			if (elm.clientHeight !== elm.scrollHeight) {
				elm.classList.add("mdln-has-scroll");
			}
		});

		// HAHA!
		//const summaries = document.querySelectorAll(".accordion-button");
		//summaries.forEach((summary) => {
		//	const audio = new Audio("https://cdn.pixabay.com/audio/2022/03/15/audio_6361c11300.mp3");
		//	summary.addEventListener("click", (e) => {
		//		audio.pause();
		//		audio.currentTime = 0.25;
		//		audio.play();
		//	});
		//});

		const classHidden = "cl-v-behave--hide";
		const classText = "cl-cell";
		const toggles = document.querySelectorAll(".cl-v-btn--toggle-next");
		Modelyn.require([Modelyn.modules.bootstrap.collapse], (bs) => {
			toggles.forEach((entry: HTMLElement) => {
				const next = (): HTMLElement => {
					let target: HTMLElement, parent = entry.parentElement;
					do {
						target = parent.nextSibling as HTMLElement;
						if (target) {
							if (target.classList.contains(classText)) return target;
							target = target.querySelector("." + classText);
						}
						parent = parent.parentElement;
					} while (parent && !target)
					return target;
				}
				const target = next();
				if (!target) return;
				if (target.classList.contains(classHidden)) {
					target.classList.replace(classHidden, "collapse");
				}
				else {
					entry.classList.add("collapsed");
					target.classList.add("show");
				}
				const bsCollapse = new bs.Collapse(target, { toggle: false });
				entry.addEventListener("click", function () {
					entry.classList.toggle("collapsed");
					bsCollapse.toggle();
				});
			});

		});

		Modelyn.AlignGrid();
		Modelyn.BlockLink();
		Modelyn.AnimIn();
		Modelyn.AnimOver();
	}

	static UrlStripDomain(url: string): string {
		let p1: number;
		if ((p1 = url?.indexOf("//")) > 0) {
			return url.substring(url.indexOf("/", p1 + 2));
		}
		return url;
	}

	static Css(element: HTMLElement, styles: Record<string, string | number>): void {
		for (const property in styles) {
			element.style[property] = styles[property].toString();
		}
	}

	/**
	 * NOTE: This only works for lazy loaded images that are responsive!
	 * @param imgSelector a selector or HTMLElement
	 * @param imgOrUrl a URL string or HTMLElement
	 * @param fadeTime the time to animate the fade
	 */
	static SwapImage(imgSelector: string | HTMLElement, imgOrUrl: string | HTMLElement, fadeTime: number = 200): void {
		//console.error("-- CALL SwapImage", arguments);

		if (!imgOrUrl)
			return;
		let newPath: string = null, sourceImg: HTMLImageElement | HTMLVideoElement = null;
		if (typeof imgOrUrl === "string") {
			newPath = imgOrUrl;
		}
		else {
			if ((imgOrUrl as HTMLElement).tagName === "A") {
				sourceImg = (imgOrUrl as HTMLElement).firstElementChild as HTMLImageElement | HTMLVideoElement;
			}
			else {
				sourceImg = imgOrUrl as HTMLImageElement | HTMLVideoElement;
			}
			if (!sourceImg || (sourceImg.tagName !== "IMG" && sourceImg.tagName !== "VIDEO")) {
				console.error("SwapImage source must be an IMG or VIDEO", imgOrUrl);
				return;
			}
			newPath = this.UrlStripDomain(sourceImg.currentSrc).split(/\?/)[0];
		}

		if (newPath.indexOf("?") > -1) {
			console.error("Cannot swap with parameters: " + newPath);
			return;
		}

		let $targetImg = $(imgSelector as any) as JQuery<HTMLElement>;

		if (!imgSelector || $targetImg.length !== 1 || ($targetImg[0].tagName !== "IMG" && $targetImg[0].tagName !== "VIDEO")) {
			console.error("SwapImage must be called on an single IMG or VIDEO: " +
				($targetImg.length === 0 ? "No image found" :
					$targetImg.length > 1 ? $targetImg.length + " images found" : "Bad image - its " + $targetImg[0].tagName), $targetImg, imgSelector);
			return;
		}
		if (!global.lazySizes) {
			console.error("SwapImage only works with lazy loading on");
			return;
		}

		const swapUrl = (url: string, val: string) => {
			const split = url.split(/\?/);
			split[0] = val;
			return split.join("?");
		}
		const swapAttr = (elm: JQuery<HTMLElement>, name: string) => {
			elm.attr(name, swapUrl(elm.attr(name) || "", newPath));
		}
		swapAttr($targetImg, "data-srchighres");
		let targetImg = $targetImg[0] as HTMLImageElement | HTMLVideoElement;
		const host = (targetImg as any);

		const fit = targetImg.getAttribute("data-parent-fit");

		const lazyTarget = targetImg.classList.contains("mdln-lazy");
		if (lazyTarget) {
			global.lazySizes.loader.unveil(targetImg);
		}

		let currentSrc = this.UrlStripDomain(targetImg.dataset.src || (targetImg instanceof HTMLImageElement && targetImg.srcset ? targetImg.currentSrc : null) || targetImg.src);

		const split = currentSrc.split(/\?/);

		if ((!host.newImg && split[0] === newPath) || (host.newImg && host.newPath === newPath)) {
			//console.log("same: " + imgOrUrl);
			return;
		}

		let video = false, useVideo = false;
		let newSrc = swapUrl(currentSrc, newPath).replace(/&h=\d+/, "");
		if (sourceImg) {
			// Replace etag (sanity and in case same size no 2 x load)
			const etag = this.UrlGetParam("etag", sourceImg.currentSrc);
			if (etag) {
				newSrc = newSrc.replace(/etag=[^&]*/g, "etag=" + etag);
			}
			if (sourceImg.tagName === "VIDEO" || sourceImg.dataset.mime?.startsWith("video/")) {
				video = true;
			}
		}
		else if (newPath.endsWith(".mp4")) {
			video = true;
		}
		if (video && fit) {
			//newSrc = newSrc.replace(/type=GenerateThumb/g, "type=Thumb");
			//newSrc = this.UrlAddParam("type", "Thumb", newSrc);
			newSrc = this.urlParams([["type", null]], newSrc);
			useVideo = true;
		}
		else {
			//newSrc = newSrc.replace(/type=Thumb/g, "type=GenerateThumb");
			newSrc = this.urlParams([["type", "GenerateThumb"]], newSrc);
		}

		// if current - stop and clean
		if (host.newImg) {
			//console.log("cancel");
			$(host.newImg).off("lazyloaded");
			$(host.newImg).off("transitionend");

			if (host.loaded && 0) {
				host.loaded = false;
				targetImg.parentElement.replaceChild(host.newImg, targetImg);
				host.newImg.setAttribute("id", targetImg.id);

				this.SwapImage(imgSelector, imgOrUrl, fadeTime);
				return;
			}
			else {
				host.newImg.src = "";
				host.newImg.srcset = "";
				host.newImg.abort = true;
				host.newImg.remove();
			}
			host.newImg = null;

			host.loaderElm.remove();
			host.newContainer.remove();

			this.Css(targetImg, { transition: "none" });
			targetImg.offsetWidth; // DOM
			this.Css(targetImg, { height: "", width: "", opacity: "1" });
		}
		host.newPath = newPath;

		const $a = $targetImg.parent();
		const hasLink = $a[0].tagName === "A";
		const easyZoom = hasLink && $a.parent().data("easyZoom");

		let addTo = targetImg.parentElement;

		if (hasLink) {
			if (addTo !== targetImg.offsetParent) {
				addTo = addTo.parentElement;
			}
			swapAttr($a, "href");
		}
		if (easyZoom) {
			addTo = addTo.parentElement;
		}

		targetImg.style.aspectRatio = "";
		//targetImg.offsetWidth;
		const targetRect = targetImg.getBoundingClientRect();
		this.Css(targetImg, { width: (targetRect.width) + "px", height: (targetRect.height) + "px" });
		$targetImg.removeAttr("width").removeAttr("height");

		const newContainer = document.createElement("div");
		newContainer.style.position = "absolute";
		newContainer.className = "mdln-temp";

		const newImg = document.createElement(useVideo ? "video" : "img") as HTMLImageElement | HTMLVideoElement;
		for (const attr of targetImg.attributes) {
			if (["id", "width", "height", "src", "data-src", "srcset", "data-srcset", "sizes", "data-sizes", "data-mw", "data-aspectratio"].indexOf(attr.name) > -1) {
				continue;
			}
			if (!useVideo && ["controls", "autoplay", "loop", "muted", "playsinline", "poster", "controlslist", "disablepictureinpicture", "disableremoteplayback"].indexOf(attr.name.toLowerCase()) > -1) {
				continue;
			}
			newImg.setAttribute(attr.name, attr.value);
		}
		if (useVideo) {
			newImg.setAttribute("autoplay", "1");
			newImg.setAttribute("loop", "1");
		}

		this.Css(newImg, { visibility: "hidden", width: "", height: "" });

		newImg.classList.remove("lazyautosizes", "lazyloaded", "lazyloading");
		if (sourceImg && sourceImg.getAttribute("data-mw")) {
			newImg.setAttribute("data-mw", sourceImg.getAttribute("data-mw"));
		}
		if (sourceImg && sourceImg.dataset.mime) {
			newImg.setAttribute("data-mime", sourceImg.dataset.mime);
		}

		const addRect = addTo.getBoundingClientRect();
		let width = addRect.width, height = addRect.height;

		if (targetImg.clientWidth <= 1 || targetImg.clientHeight <= 1) {
			this.Css(targetImg, { width: width + "px", height: height + "px" });
		}

		if (fit) {
			const style = (<any>addTo).currentStyle || getComputedStyle(addTo);
			width -= parseFloat(style.paddingLeft) + parseFloat(style.paddingRight) + parseFloat(style.borderLeftWidth) + parseFloat(style.borderRightWidth);
			height -= parseFloat(style.paddingTop) + parseFloat(style.paddingBottom) + parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);

			newContainer.style.width = (width) + "px";
			if (fit !== "width") {
				newContainer.style.height = (height) + "px";
				if (fit === "contain") {
					//newSrc = this.UrlAddParam("h", Math.round(height), newSrc);
				}
			}
		}
		else {
			newImg.style.width = (targetRect.width) + "px";
			newContainer.style.width = (targetRect.width) + "px";
			newContainer.style.height = (targetRect.height) + "px";
		}

		if (targetImg.offsetTop > addTo.offsetTop) {
			newContainer.style.left = targetRect.left + "px";
			newContainer.style.top = targetRect.top + "px";
		}

		//newContainer.style.backgroundColor = "rgba(200, 200, 100, 0.5)";

		if (sourceImg) {
			const aspectRatio = sourceImg.getAttribute("data-aspectratio");
			newImg.setAttribute("data-aspectratio", aspectRatio);

			const hsRatio = sourceImg.getAttribute("data-hs-ratio");
			if (hsRatio) {
				newImg.setAttribute("data-hs-ratio", hsRatio);
			}
			if (aspectRatio) {
				newImg.style.aspectRatio = aspectRatio;
			}
			else {
				newImg.style.aspectRatio = "";
			}
		}
		else {
			newImg.style.aspectRatio = "";
		}

		this.Css(newImg, { transition: "none" });

		// Add loader
		const loaderElm = document.createElement("div");
		loaderElm.className = "mdln-loader";
		this.Css(loaderElm, {
			//backgroundColor: "rgba(100, 100, 100, 0.05)",
			width: (Math.min(targetRect.width, addRect.width) - 4) + "px",
			height: (Math.min(targetRect.height, addRect.height) - 4) + "px",
			left: 0, right: 0
		});

		let newW: number = null, newH: number = null;
		if (fit === "width" && sourceImg) {
			const mw = parseInt(sourceImg.getAttribute("data-mw"));
			if (mw && mw < newContainer.clientWidth) {
				newW = mw;
				newImg.style.width = newW + "px";
				const ar = parseFloat(newImg.getAttribute("data-aspectratio"));
				if (ar) {
					//console.log("AR", ar, mw, newH, newW * ar, newImg, newContainer);
					newH = newW / ar;
					newImg.style.height = newH + "px";
				}
			}
		}

		newImg.setAttribute("data-sizes", "auto");
		newImg.setAttribute("data-src", newSrc);

		host.newImg = newImg;
		host.loaderElm = loaderElm;
		host.newContainer = newContainer;

		const newImgLoaded = (ev) => {
			if ((ev.target as any) !== newImg) {
				console.log("!!!");
				return;
			}

			loaderElm.remove();

			host.loaded = true;

			newImg.style.aspectRatio = "";
			newImg.offsetWidth;

			const newRect = newImg.getBoundingClientRect();
			if (!newW) newW = newRect.width;
			if (!newH) newH = newRect.height;

			//console.log("old " + fit, targetImg.currentSrc, `w ${targetImg.naturalWidth}, h ${targetImg.naturalHeight}`);
			//console.log("new " + fit, newImg.currentSrc, `w ${targetImg.naturalWidth}, h ${targetImg.naturalHeight}`);
			//console.log(targetRect, newRect); // !!!!!!!!!!

			if (newW <= 0 || newH <= 0) {
				console.error("SwapImage should have an offset/measurable dom before running", newW, newH);
				return;
			}

			//this.Css(newImg, { aspectRatio: "" });
			this.Css(newImg, { width: targetRect.width + "px", height: targetRect.height + "px" });;
			// NOTE: Force the browser to apply the CSS size before starting the transition - alternative to setTimeout
			newImg.offsetWidth;

			const finalSwap = () => {
				if (!targetImg.parentElement) {
					newContainer?.remove();
					return;
				}

				newImg.style.transition = "none";
				this.Css(newImg, { height: "", width: "" });
				//newImg.offsetWidth;

				host.loaded = host.loaderElm = host.newContainer = host.newPath = null;
				if (!host.newImg) return;
				host.newImg = null;

				targetImg.parentElement.replaceChild(newImg, targetImg);
				newImg.setAttribute("id", targetImg.id);

				newContainer?.remove();

				if (easyZoom) {
					easyZoom.swap(newSrc, newPath, targetImg instanceof HTMLImageElement ? targetImg.srcset : "");
				}
			};

			if (fadeTime <= 0) {
				this.Css(newImg, { visibility: "visible", width: newW + "px", height: newH + "px", opacity: "1", });
				finalSwap();
			}
			else {
				this.Css(newImg, { visibility: "visible", opacity: "0" });

				$(newImg).one("transitionend", () => finalSwap());

				this.Css(targetImg, { transition: `all ${fadeTime}ms`, opacity: "0", width: newW + "px", height: newH + "px" });
				this.Css(newImg, { transition: `all ${fadeTime}ms`, opacity: "1", width: newW + "px", height: newH + "px" });
			};
		};

		if (useVideo) {
			$(newImg).one("loadeddata", newImgLoaded);
		}
		else {
			$(newImg).one("lazyloaded", newImgLoaded);
		}

		newContainer.appendChild(loaderElm);
		newContainer.appendChild(newImg);
		addTo.prepend(newContainer);

		if (lazyTarget && targetImg.tagName === "IMG" && !targetImg.classList.contains("lazyloaded")) {
			$(targetImg).one("lazyloaded", (ev) => {
				global.lazySizes.loader.unveil(newImg);
			});
		}
		if (useVideo) {
			newImg.src = newSrc;
		}
		else {
			global.lazySizes.loader.unveil(newImg);
		}
	}

	static autoHeight(elm: HTMLElement) {
		if (!elm) return;
		if (!(elm as any).autoHeight) {
			const grow = (ev: Event) => {
				/*
				const hasVertScroll = elm.scrollWidth > elm.offsetWidth;
				if (elm.scrollHeight > elm.clientHeight || (hasVertScroll && !(elm as any).hasVertScroll)) {
					(elm as any).hasVertScroll = hasVertScroll;
					const dif = elm.offsetHeight - elm.clientHeight;
					const h = (elm.scrollHeight + dif);
					if (h > 1) {
						elm.style.height = h + "px";
					}
				}
				*/
				elm.style.height = "auto";
				elm.style.height = `${elm.scrollHeight + 2}px`;
			}
			(elm as any).autoHeight = grow;
			//elm.style.resize = "none";
			//elm.style.overflow = "hidden";
			grow(null);
			elm.addEventListener("input", grow);

			const proto = Object.getPrototypeOf(elm);
			const valueDescriptor = Object.getOwnPropertyDescriptor(proto, 'value');

			Object.defineProperty(elm, "value", {
				get() { return valueDescriptor.get.call(this); },
				set(newVal) {
					valueDescriptor.set.call(this, newVal);
					grow(null);
				}
			});

			let resizeTimeout = 0;
			window.addEventListener("resize", () => {
				clearTimeout(resizeTimeout);
				resizeTimeout = window.setTimeout(() => grow(null), 100);
			});
		}
	}

	static async initCarousel(type: string, id: string, interval: string, dots: string, options: any, callback?: (caro: any) => void) {
		switch (type) {
			case "fb":
				const ctrl = document.getElementById(id);
				if (!ctrl) return;
				// "css!/client/scss/parts/_modelyn-fancybox.scss.css"
				const path = "/node_modules/@fancyapps/ui/dist/";
				const loads = !global.Carousel ? [`iife!${path}carousel/carousel.umd.js`, `css!${path}carousel/carousel.css`] : [];

				if (!global.Arrows) loads.push(...[`iife!${path}carousel/carousel.arrows.umd.js`, `css!${path}carousel/carousel.arrows.css`]);

				let useThumbs = dots === "thumbs";
				let useDots = dots === "true";

				if (useThumbs) {
					if (!global.Thumbs) loads.push(...[`iife!${path}carousel/carousel.thumbs.umd.js`, `css!${path}carousel/carousel.thumbs.css`]);
					options.Thumbs = {
						type: "classic",
						//parentEl: ctrl,
						Carousel: {
							dragFree: !global.isPle,
							slidesPerPage: 'auto',
							Navigation: true,
							//axis: 'y',
						}
					};
				}
				else if (useDots) {
					if (!global.Dots) loads.push(...[`iife!${path}carousel/carousel.dots.umd.js`, `css!${path}carousel/carousel.dots.css`]);
					options.Dots = {
						dynamicFrom: global.isPle ? 20 : 7
					};
				}

				const useAutoScroll = interval && interval.startsWith("s") && !global.isPle;
				const useAutoPlay = !useAutoScroll && interval && !global.isPle;

				if (useAutoScroll) {
					const speed = parseInt(interval.substring(1));
					if (!global.Autoscroll) loads.push(`iife!${path}carousel/carousel.autoscroll.umd.js`);
					options.Autoscroll = {
						speed: speed,
						speedOnHover: 1,
						hoverPause: true,
						autoStart: speed > 0,
						progressParentEl: ctrl
					};
				}
				else if (useAutoPlay) {
					const speed = parseInt(interval);
					if (!global.Autoplay) loads.push(`iife!${path}carousel/carousel.autoplay.umd.js`);
					options.Autoplay = {
						timeout: speed,
						hoverPause: true,
						autoStart: speed > 0,
						progressParentEl: ctrl
					};
				}

				await this.requireAsync(loads);

				const Carousel = global.Carousel;

				// fix default
				options.fill = options.fill || options.fill === undefined;

				window["carousel_" + id] = Carousel(ctrl, {
					...{
						dragFree: global.isPle,
						transition: "crossfade",
						slidesPerPage: options.fill ? "auto" : 1,
						//slidesPerPage: "auto",
						//transformParent: true,
						on: {
							//init: (c) => { ctrl.querySelectorAll(".mdln-dummy").forEach((elm) => elm.parentNode.removeChild(elm)); },
							ready: (c) => {
								if (callback) {
									callback(c);
								}
								//if (global.isPle) global.Editor.root.clearHelpers();
							},
							render: (instance, eventName) => {
								if (global.isPle) global.Editor.root.updateHelpers(true);
							},
							settle: (carousel) => {
								if (global.isPle) global.Editor.root.updateHelpers();
							},
						}
					},
					...options
				}, {
					Dots: useDots ? global.Dots : null,
					Thumbs: useThumbs ? global.Thumbs : null,
					Autoplay: useAutoPlay ? global.Autoplay : null,
					Autoscroll: useAutoScroll ? global.Autoscroll : null,
					Arrows: global.Arrows
				}).init();

				//console.log(window["carousel_" + id]);
				break;
		}
		//if (type === "bs") {
		//	this.require([this.modules.bootstrap.carousel], () => {
		//		($("#" + id) as any).carousel({
		//			interval: global.isPle ? 0 : interval,
		//			keyboard: global.isPle ? false : true
		//		}).on("slide.bs.carousel", () => {
		//			if (global.isPle) global.Editor.root.clearHelpers();
		//		});
		//	});
		//}
	}

	static async initSelect(opt: TagsOptions): Promise<void> {
		await Modelyn.requireAsync([this.modules.kendo.multiselect]);
		const id = opt.id;
		const maxShown = parseInt(`0${opt.maxShown}`);
		const selLen = () => { return $(`#${id} option:selected`).length; };
		const autoPb = () => { if (!opt.postbackOnClose) return; global.__doPostBack(id, ""); };
		let changes: boolean, open: boolean;
		const singleSel = maxShown && selLen() > maxShown;
		const $elm = $("#" + id);

		const globalId = "_mdlnCtrl_" + id;
		let ms = window[globalId] as kendo.ui.MultiSelect;
		if (ms && ms.destroy) {
			ms.destroy();
		}

		window[globalId] = ms = $elm.kendoMultiSelect({
			placeholder: opt.placeholder,
			animation: { close: { effects: "fadeOut", duration: 200 }, open: { effects: "fadeIn", duration: 300 } },
			height: 500,
			dataTextField: "text",
			dataValueField: "value",
			filter: "contains",

			maxSelectedItems: opt.maxSelectedItems,
			autoClose: !opt.keepOpen,
			tagMode: singleSel ? "single" : "multiple",

			tagTemplate: singleSel ?
				(opt.tagTemplateSingle ? $(`#${opt.tagTemplateSingle}`).html() : null) :
				(opt.tagTemplate ? $(`#${opt.tagTemplate}`).html() : null),
			itemTemplate: opt.itemTemplate ? $(`#${opt.itemTemplate}`).html() : null,
			footerTemplate: opt.footerTemplate ? $(`#${opt.footerTemplate}`).html() : null,

			change: (ev) => {
				Modelyn.ThrottleEvent(ev.sender, () => {
					if (ev.sender.options.tagMode !== "single" && maxShown && selLen() > maxShown) {
						ev.sender.setOptions({
							tagMode: "single",
							tagTemplate: opt.tagTemplateSingle ? $(`#${opt.tagTemplateSingle}`).html() : null
						}); ev.sender.refresh();
					}
				}, 500);
				changes = true;
				if (!open) {
					autoPb();
				}
			},
			close: (ev) => {
				open = false;
				//if (opt.maxSelectedItems) {ev.sender.setOptions({ maxSelectedItems: null });}
				if (changes) {
					autoPb();
				}
			},

			open: (ev) => {
				open = true;
				//if (opt.maxSelectedItems) { ev.sender.setOptions({ maxSelectedItems: opt.maxSelectedItems }); }
				if (opt.onClick) {
					opt.onClick.call(ev.sender.element[0], ev.sender, ev.sender.value(), ev);
				}
			}

		}).data("kendoMultiSelect");

		if (opt.onInit) {
			opt.onInit.call(ms.element[0], ms);
		}
	}
}

namespace Modelyn {

	/**
	 * Show modal dialogs
	 * @typedef {Dialog}
	 */
	export class Dialog {

		/**
		 * Hides/closes all open dialogs
		 */
		static HideAll(): void {
			Modelyn.require([Modelyn.modules.bootstrap.modal], (bs) => {
				document.querySelectorAll(".modal.show").forEach((elm) => {
					bs.Modal.getInstance(elm)?.hide();
				});
			});
		}

		/**
		 * Shows a dialog with a set of options
		 *
		 * @param {Modelyn.Dialog.IOptions} options
		 */
		static async Show(options: Dialog.IOptions): Promise<HTMLElement> {
			if (Modelyn.fef !== "bs5") {
				alert(options.message);
				return;
			}
			// REF: GKKST57Y
			await Modelyn.requireAsync(Modelyn.modules.bootstrap.modal);
			// Bootstrap registers the jQuery modal bridge on DOMContentLoaded when imported early.
			if (!($ as any).fn?.modal && document.readyState === "loading") {
				await new Promise<void>(resolve => document.addEventListener("DOMContentLoaded", () => resolve(), { once: true }));
			}
			await Modelyn.requireAsync(Modelyn.modules.bootstrap.dialog);
			const bb = (window as any).bootbox;

			//global.bootstrap = bs;
			if (options.theme > 0) {
				const pre = `<i class="modal-icon"></i>`;
				if (options.title) {
					options.className = (options.className ?? "") + ` modal-title-theme-${Dialog.Theme[options.theme].toLowerCase()}`;
					options.title = pre + options.title;
				}
				else {
					options.className = (options.className ?? "") + ` modal-content-theme-${Dialog.Theme[options.theme].toLowerCase()}`;
					options.message = pre + options.message;
				}
			}
			options.locale = options.locale || Modelyn.language;

			switch (options.type) {
				case Dialog.Type.Alert:
					bb.alert(options);
					return;
				case Dialog.Type.Confirm:
					bb.confirm(options);
					return;
			}
			const dialog = bb.dialog(options);
			return dialog[0];
		}

		private static alert(message: string, theme: Dialog.Theme = null): void {

			this.Show({
				type: Dialog.Type.Alert,
				message: message,
				theme: theme,
				backdrop: true,
				closeButton: false
			});
		}

		static Alert(message: string): void { this.alert(message); }
		static Info(message: string): void { this.alert(message, Dialog.Theme.Info); }
		static Warning(message: string): void { this.alert(message, Dialog.Theme.Warning); }
		static Success(message: string): void { this.alert(message, Dialog.Theme.Success); }
		static Danger(message: string): void { this.alert(message, Dialog.Theme.Danger); }
	}

	export namespace Dialog {

		export enum Type {
			Dialog,
			Alert,
			Confirm,
		}

		export enum Theme {
			Default,
			Light,
			Dark,
			Primary,
			Info,
			Success,
			Warning,
			Danger,
		}

		export enum Size {
			Small = "sm",
			Large = "lg",
			ExtraLarge = "xl",
		}

		export interface IOptions {
			/** The type of dialog */
			type?: Dialog.Type;
			/** The theme */
			theme?: Dialog.Theme;
			locale?: string;
			/**
			 * The width of the dialog
			 *
			 * @type {?Modelyn.Dialog.Size}
			 */
			size?: Dialog.Size;
			/**
			 * The dialog title.
			 */
			title?: string;
			message: string;
			className?: string;
			/** Whether the dialog should be have a backdrop or not. Also determines whether clicking on the backdrop dismisses the modal. Default is true.
			 * - `"static"`: The backdrop is displayed.
			 * - `true`*: The backdrop is displayed, and clicking on it dismisses the dialog.
			 * - `false`: The backdrop is not displayed.
			 */
			backdrop?: boolean | "static";
			keyboard?: boolean;
			/**
			 * Shows the close button top right. Default is true.
			 *
			 * @type {?boolean}
			 */
			closeButton?: boolean;
			centerVertical?: boolean;
			buttons?: any;
			onHide?: (ev: any) => void;
			callback?: (result: any) => void;
		}
	}

	export namespace Messaging {
		export enum Permissions {
			granted = "granted",
			denied = "denied",
			ask = "default",
			notSupported = "notSupported",
		}
	}
}

//type ModelynType = typeof Modelyn;

declare global {
	const Modelyn: typeof import("./modelyn").Modelyn;
	//var Modelyn: typeof Modelyn;
}

export { Modelyn };

Modelyn.init();


